<template>
	<div class="card">
		<!-- Top Controls Section -->
		<div class="header-section">
			<div class="controls-section">
				<div class="control-group">
					<!-- First Row -->
					<div class="control-row">
						<div class="checkbox-wrapper">
							<Checkbox v-model="templateStore.isDefaultTemplate" binary />
							<label>Default Template</label>
						</div>
						<div class="template-selector-container">
							<!-- Dropdown mode -->
							<div v-if="!isEditingThemeName" class="selector-with-edit">
								<Select
									v-model="selectedTemplateId"
									:options="templates"
									option-label="templateName"
									option-value="id"
									placeholder="Select Template"
									class="template-select"
									@change="loadTemplateFromDB"
								/>
								<Button
									class="edit-name-icon-button"
									@click="startEditingThemeName"
									:disabled="!selectedTemplateId || (isStandardTheme && !overrideStandardTheme)"
									size="small"
									severity="secondary"
									outlined
									v-tooltip.top="getEditNameTooltip()"
								>
									<i class="pi pi-pencil"></i>
								</Button>
							</div>

							<!-- Edit mode -->
							<div v-else class="edit-name-container">
								<InputText
									v-model="editingThemeName"
									placeholder="Enter theme name"
									class="theme-name-input"
									@keyup.enter="saveInlineThemeName"
									@keyup.escape="cancelEditingThemeName"
									ref="themeNameInputRef"
								/>
								<Button class="save-name-button" @click="saveInlineThemeName" :disabled="!editingThemeName.trim()" size="small" severity="success">
									<i class="pi pi-check"></i>
								</Button>
								<Button class="cancel-name-button" @click="cancelEditingThemeName" size="small" severity="secondary" outlined> Cancel </Button>
							</div>
						</div>
						<Button
							class="save-button"
							@click="loadTemplateFromDB"
							:disabled="isStandardTheme && !overrideStandardTheme"
							:class="{ disabled: isStandardTheme && !overrideStandardTheme }"
							v-tooltip.top="'Discard unsaved changes and reload the current theme from the database'"
						>
							<i class="pi pi-circle"></i>
							Cancel
						</Button>
						<Button
							class="save-button"
							@click="saveTheme"
							:disabled="isStandardTheme && !overrideStandardTheme"
							:class="{ disabled: isStandardTheme && !overrideStandardTheme }"
							v-tooltip.top="'Save all changes to the current theme'"
						>
							<i class="pi pi-save"></i>
							Save
						</Button>
						<div class="right-actions">
							<Button
								class="delete-theme-button save-button"
								@click="confirmDeleteTheme"
								severity="danger"
								:disabled="!selectedTemplateId || (isStandardTheme && !overrideStandardTheme)"
								:class="{ disabled: !selectedTemplateId || (isStandardTheme && !overrideStandardTheme) }"
								v-tooltip.top="getDeleteTooltip()"
							>
								<i class="pi pi-trash"></i>
								Delete Theme
							</Button>

						<!-- <div class="vertical-divider"></div>

						
						<Button
							class="color-scheme-generator-button save-button"
							@click="openColorSchemeGeneratorDialog"
							severity="help"
							:disabled="isStandardTheme && !overrideStandardTheme"
							:class="{ disabled: isStandardTheme && !overrideStandardTheme }"
						>
							<i class="pi pi-palette"></i>
							Generate Color Scheme
						</Button>

						
						<div class="vertical-divider"></div> -->

							<Button
							class="create-theme-button save-button"
							@click="openCreateThemeDialog"
							severity="secondary"
							v-tooltip.top="'Create a new theme starting from an existing theme as a base — the new theme will be a copy you can customise independently'">
								<i class="pi pi-plus"></i>
								Create New Theme
							</Button>

							<Button
							class="export-theme-button"
							@click="exportTheme"
							severity="info"
							:disabled="!selectedTemplateId"
							v-tooltip.top="'Export the current theme to a .lcat file — use it to share or restore this theme in another environment'">
								<i class="pi pi-upload"></i>
								Export Theme
							</Button>

							<Button
							class="import-theme-button"
							@click="triggerImportFile"
							severity="info"
							:disabled="!selectedTemplateId"
							v-tooltip.top="'Import a theme from a .lcat file — the imported settings will overwrite the currently selected theme'">
								<i class="pi pi-download"></i>
								Import Theme
							</Button>
						</div>

						<input type="file" ref="importFileInput" accept=".lcat" @change="handleImportFile" style="display: none" />
					</div>

					<!-- Quick Color Picker Buttons -->
					<!-- <div class="quick-color-pickers">
						<div class="quick-color-item" v-for="colorKey in quickColorKeys" :key="colorKey">
							<span class="quick-color-description">{{ colorKey }}</span>
							<div 
								class="quick-color-swatch" 
								:style="{ backgroundColor: getQuickColorValue(colorKey) }"
								@click="toggleQuickColorPicker(colorKey)"
								:title="`Edit ${colorKey} color`"
							>
							</div>
							<div v-if="showQuickPicker === colorKey" class="quick-color-picker-container" @click.stop>
								<ColorPicker
									v-model="quickColorValues[colorKey]"
									format="hex"
									class="quick-color-picker"
									@change="(newColor) => updateQuickColor(colorKey, newColor)"
									@update:model-value="(newColor) => updateQuickColor(colorKey, newColor)"
									:inline="true"
								/>
								<div class="gradient-picker-controls">
									<button
										@click="showQuickGradientPicker($event, colorKey)"
										class="gradient-picker-button"
										title="Select from gradients"
									>
										<i class="pi pi-th-large"></i>
										Gradients
									</button>
								</div>
							</div>
						</div>
					</div> -->
				</div>
			</div>
		</div>

		<!-- Standard Theme Warning -->
		<div v-if="isStandardTheme" class="standard-theme-warning">
			<div class="warning-content">
				<i class="pi pi-exclamation-triangle warning-icon"></i>
				<span class="warning-text">
					This is a standard theme and should not be modified directly. Changes may be overwritten during system updates.
				</span>
				<div class="warning-controls">
					<label class="override-checkbox">
						<Checkbox v-model="overrideStandardTheme" binary />
						<span>Override protection and allow editing</span>
					</label>
				</div>
			</div>
		</div>

		<div class="main-layout">
			<!-- Color Palette Sidebar (hidden - not needed currently) -->
			<div class="color-sidebar" v-show="false">
				<div class="color-palette-section">
					<h3 class="sidebar-title">{{ isDefaultPalette ? "Default " : "" }}Color Palette</h3>

					<div class="palette-controls">
						<Button v-if="!isDefaultPalette" @click="resetToDefaultPalette" class="reset-palette-button" severity="secondary" size="small">
							<i class="pi pi-refresh"></i>
							Reset to Default
						</Button>
					</div>

					<div class="color-grid-vertical">
						<!-- Semantic Colors Container -->
						<div class="semantic-colors-container">
							<div class="semantic-colors-grid">
								<div v-for="colorNumber in ['0', '1', '2'] as ColorNumber[]" :key="colorNumber" class="color-item-vertical semantic-color-item">
									<div class="color-item-row semantic-color-row">
										<div class="color-special-label">{{ getColorLabel(colorNumber) }}</div>
										<div class="color-swatch-controls" style="position: relative">
											<div class="color-swatch-wrapper" @click.left="selectColor(colorValues[colorNumber], colorNumber)">
												<div
													class="color-swatch"
													:style="{ backgroundColor: colorValues[colorNumber] }"
													:class="{
														'selected-crosshair': selectedColorNumber === colorNumber,
														'selected-picker': selectedColorForPickerNumber === colorNumber,
													}"
												></div>
											</div>
											<button
												@click="toggleColorPicker(colorNumber)"
												class="color-edit-button"
												:class="{ active: showPicker === colorNumber }"
												title="Edit color"
											>
												<i class="pi pi-palette"></i>
											</button>
											<button
												@click="toggleGradations($event, colorNumber)"
												class="color-gradations-button"
												:class="{ active: showGradations[colorNumber] }"
												title="Show gradations"
											>
												<i class="pi pi-chevron-right"></i>
											</button>
										</div>
									</div>
									<div v-if="showPicker === colorNumber" class="color-picker-container-below" @click.stop>
										<ColorPicker
											v-model="colorValues[colorNumber]"
											format="hex"
											class="color-picker-numbered"
											:inline="true"
											@change="(newColor) => updateColorValue(colorNumber, newColor)"
											@update:model-value="(newColor) => updateColorValue(colorNumber, newColor)"
										/>
										<div class="hex-input-container">
											<InputText
												:id="'hexInput-' + colorNumber"
												v-model="hexInputValues[colorNumber]"
												@input="onHexInputChange(colorNumber)"
												@blur="validateAndApplyHex(colorNumber)"
												class="hex-input"
												placeholder="#ffffff"
												maxlength="7"
											/>
										</div>
										<div class="gradient-picker-controls">
											<button @click="showGradientPicker($event, colorNumber)" class="gradient-picker-button" title="Select from gradients">
												<i class="pi pi-th-large"></i>
												Gradients
											</button>
										</div>
									</div>
								</div>
							</div>

							<!-- Apply Semantic Colors Button -->
							<div class="apply-semantic-colors-button-container">
								<Button
									label="Apply to Theme"
									icon="pi pi-palette"
									class="apply-semantic-colors-button"
									@click="openApplySemanticColorsDialog"
									title="Apply these colors to the entire theme"
								/>
							</div>
						</div>

						<!-- Spacing between semantic and regular colors -->
						<div class="colors-spacing"></div>

						<!-- Regular Colors -->
						<div class="regular-colors-grid">
							<div
								v-for="colorNumber in ['3', '4', '5', '6', '7', '8', '9', '10', '11'] as ColorNumber[]"
								:key="colorNumber"
								class="color-item-vertical"
							>
								<div class="color-item-row">
									<div class="color-swatch-controls">
										<div class="color-swatch-wrapper" @click.left="selectColor(colorValues[colorNumber], colorNumber)">
											<div
												class="color-swatch"
												:style="{ backgroundColor: colorValues[colorNumber] }"
												:class="{
													'selected-crosshair': selectedColorNumber === colorNumber,
													'selected-picker': selectedColorForPickerNumber === colorNumber,
												}"
											></div>
										</div>
										<button
											@click="toggleColorPicker(colorNumber)"
											class="color-edit-button"
											:class="{ active: showPicker === colorNumber }"
											title="Edit color"
										>
											<i class="pi pi-palette"></i>
										</button>
									</div>
									<div class="color-special-label">{{ getColorLabel(colorNumber) }}</div>
								</div>
								<div v-if="showPicker === colorNumber" class="color-picker-container-below" @click.stop>
									<ColorPicker
										v-model="colorValues[colorNumber]"
										format="hex"
										class="color-picker-numbered"
										:inline="true"
										@change="(newColor) => updateColorValue(colorNumber, newColor)"
										@update:model-value="(newColor) => updateColorValue(colorNumber, newColor)"
									/>
									<div class="hex-input-container">
										<InputText
											:id="'hexInput-' + colorNumber"
											v-model="hexInputValues[colorNumber]"
											@input="onHexInputChange(colorNumber)"
											@blur="validateAndApplyHex(colorNumber)"
											class="hex-input"
											placeholder="#ffffff"
											maxlength="7"
										/>
									</div>
									<div class="gradient-picker-controls">
										<button @click="showGradientPicker($event, colorNumber)" class="gradient-picker-button" title="Select from gradients">
											<i class="pi pi-th-large"></i>
											Gradients
										</button>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Main Content Area -->
			<div class="content-area">
				<Tabs v-model:value="selectedThemeMainTab">
					<TabList :value="selectedThemeMainTab" @change="selectedThemeMainTab = $event.value" class="tab-list-with-undo">
						<Tab v-for="(tab, index) in tabs" :value="index">{{ tab.tabName }}</Tab>
						<Button
							v-if="hasColorChanges"
							class="undo-button-inline"
							@click="undoLastChange"
							severity="success"
							v-tooltip.top="'Undo last color change'"
							size="small"
						>
							<i class="pi pi-arrow-left"></i>
							Undo
						</Button>
					</TabList>
					<TabPanels>
						<TabPanel v-for="(tab, index) in tabs" :value="index">
							<component
								:is="tab.tabPage"
								:selectedTemplate="selectedTemplate"
								:isLoadingTemplate="isLoadingTemplate"
								:paletteColors="colorValues"
								@updateTheme="updateThemeData"
								@colorSelected="handleColorSelected"
								:selectedColor="selectedColor || undefined"
								@disablePasteColor="() => disablePasteColor('', '')"
								@saveToUndo="saveThemeToHistory"
								:ref="(el) => setTabComponentRef(index, el)"
								style="z-index: 10 !important"
							></component>
						</TabPanel>
					</TabPanels>
				</Tabs>
			</div>
		</div>
	</div>

	<!-- Dialog for creating new theme -->
	<Dialog
		header="Create New Theme"
		v-model:visible="showCreateThemeDialog"
		:modal="true"
		:closable="true"
		:draggable="false"
		:style="{ width: '500px' }"
		class="create-theme-dialog"
	>
		<div class="create-theme-content">
			<div class="field">
				<label for="newThemeName" class="field-label">New theme name:</label>
				<InputText
					id="newThemeName"
					v-model="newThemeName"
					placeholder="Enter theme name"
					class="w-full"
					:class="{ 'p-invalid': !newThemeName && showValidationErrors }"
				/>
				<small v-if="!newThemeName && showValidationErrors" class="p-error">Theme name is required</small>
			</div>

			<div class="field">
				<label for="baseTheme" class="field-label">Base theme:</label>
				<Select
					id="baseTheme"
					v-model="selectedBaseTheme"
					:options="availableBaseThemes"
					option-label="label"
					option-value="value"
					placeholder="Select base theme"
					class="w-full"
					:class="{ 'p-invalid': !selectedBaseTheme && showValidationErrors }"
				/>
				<small v-if="!selectedBaseTheme && showValidationErrors" class="p-error">You must select a base theme</small>
				<small v-else class="p-help">Choose the theme to use as starting point</small>
			</div>
		</div>

		<template #footer>
			<Button label="Cancel" icon="pi pi-times" severity="secondary" @click="closeCreateThemeDialog" />
			<Button label="Create Theme" icon="pi pi-check" @click="createNewTheme" />
		</template>
	</Dialog>

	<!-- Dialog for color scheme selection -->
	<Dialog
		header="Select Color Scheme Type"
		v-model:visible="showColorSchemeDialog"
		:modal="true"
		:closable="true"
		:draggable="false"
		:style="{ width: '600px' }"
		class="color-scheme-dialog"
	>
		<div class="color-scheme-content">
			<div class="base-color-display">
				<label class="field-label">Base Color:</label>
				<div class="base-color-preview">
					<div class="color-preview-swatch" :style="{ backgroundColor: baseColorForScheme }"></div>
					<span class="color-hex-value">{{ baseColorForScheme }}</span>
				</div>
			</div>

			<div class="field">
				<label for="colorSchemeType" class="field-label">Choose color scheme type:</label>
				<Select
					id="colorSchemeType"
					v-model="selectedColorSchemeType"
					:options="colorSchemeTypes"
					option-label="label"
					option-value="value"
					placeholder="Select color scheme type"
					class="w-full"
				/>
				<small v-if="selectedColorSchemeType" class="p-help">
					{{ colorSchemeTypes.find((t) => t.value === selectedColorSchemeType)?.description }}
				</small>
				<small v-else class="p-help" style="color: #999"> Select a color scheme type above to see its description </small>
			</div>
			<!-- Detailed explanation of selected color scheme -->
			<div v-if="selectedColorSchemeType" class="color-scheme-explanation">
				<div class="explanation-card">
					<h4>{{ colorSchemeTypes.find((t) => t.value === selectedColorSchemeType)?.label }}</h4>
					<p>{{ getColorSchemeDetailedExplanation(selectedColorSchemeType) }}</p>
				</div>
			</div>
		</div>

		<template #footer>
			<Button label="Cancel" icon="pi pi-times" severity="secondary" @click="closeColorSchemeDialog" />
			<Button label="Apply Scheme" icon="pi pi-check" @click="applySelectedColorScheme" :disabled="!selectedColorSchemeType" />
		</template>
	</Dialog>

	<!-- Dialog for color scheme generator -->
	<Dialog
		header="Generate Color Scheme"
		v-model:visible="showColorSchemeGeneratorDialog"
		:modal="true"
		:closable="true"
		:draggable="false"
		:style="{ width: '600px' }"
		class="color-scheme-generator-dialog"
	>
		<div class="color-scheme-generator-content">
			<div class="generator-color-display">
				<label class="field-label">Base Color:</label>
				<div class="generator-color-controls">
					<div
						class="generator-color-swatch"
						:style="{ backgroundColor: generatorBaseColor }"
						@click="toggleGeneratorColorPicker"
						:title="'Click to change color: ' + generatorBaseColor"
					></div>
					<span class="generator-color-hex">{{ generatorBaseColor.toUpperCase() }}</span>
				</div>

				<!-- Color picker panel -->
				<div v-if="showGeneratorColorPicker" class="generator-color-picker-panel">
					<ColorPicker
						v-model="generatorBaseColor"
						class="generator-color-picker"
						:inline="true"
						@change="updateGeneratorColor"
						@update:model-value="updateGeneratorColor"
					/>
				</div>
			</div>

			<div class="field">
				<label for="generatorSchemeType" class="field-label">Choose color scheme type:</label>
				<Select
					id="generatorSchemeType"
					v-model="selectedGeneratorSchemeType"
					:options="colorSchemeTypes"
					option-label="label"
					option-value="value"
					placeholder="Select color scheme type"
					class="w-full"
				/>
			</div>

			<!-- Detailed explanation of selected color scheme -->
			<div v-if="selectedGeneratorSchemeType" class="color-scheme-explanation">
				<div class="explanation-card">
					<h4>{{ colorSchemeTypes.find((t) => t.value === selectedGeneratorSchemeType)?.label }}</h4>
					<p>{{ getColorSchemeDetailedExplanation(selectedGeneratorSchemeType) }}</p>
				</div>
			</div>
		</div>

		<template #footer>
			<Button label="Cancel" icon="pi pi-times" severity="secondary" @click="closeColorSchemeGeneratorDialog" />
			<Button label="Generate Scheme" icon="pi pi-check" @click="applyGeneratorColorScheme" :disabled="!selectedGeneratorSchemeType" />
		</template>
	</Dialog>

	<!-- Delete Confirmation Dialog -->
	<Dialog
		header="Delete Theme Confirmation"
		v-model:visible="showDeleteConfirmDialog"
		:modal="true"
		:closable="true"
		style="width: 450px"
		class="delete-confirm-dialog"
	>
		<div class="delete-confirm-content">
			<div class="confirmation-message">
				<i class="pi pi-exclamation-triangle warning-icon"></i>
				<div class="message-text">
					<p><strong>Are you sure you want to delete this theme?</strong></p>
					<p class="theme-name-display">"{{ selectedThemeToDelete?.templateName || "Unknown Theme" }}"</p>
					<p class="warning-text">This action cannot be undone. The theme will be deactivated but data will be preserved.</p>
				</div>
			</div>
		</div>
		<template #footer>
			<Button label="Cancel" severity="secondary" @click="closeDeleteConfirmDialog" outlined />
			<Button label="Delete Theme" severity="danger" @click="executeDeleteTheme" :loading="isDeletingTheme" />
		</template>
	</Dialog>

	<!-- Import Confirmation Dialog -->
	<Dialog
		header="Import Theme Confirmation"
		v-model:visible="showImportConfirmDialog"
		:modal="true"
		:closable="false"
		class="import-confirm-dialog"
		style="width: 500px"
	>
		<div class="import-confirm-content">
			<div class="confirmation-message">
				<i class="pi pi-exclamation-triangle warning-icon" style="color: #f59e0b; font-size: 2rem"></i>
				<div class="message-text">
					<h4 style="margin: 0 0 1rem 0; color: #dc2626">⚠️ Newer Theme Overwrite Warning</h4>
					<p style="margin-bottom: 1rem">
						You are trying to import an <strong>older theme</strong> into "<strong>{{ importDialogData.currentThemeName }}</strong
						>" which is more recent:
					</p>

					<div style="background: #fef3c7; padding: 1rem; border-radius: 8px; margin-bottom: 1rem; border: 1px solid #f59e0b">
						<div style="margin-bottom: 0.75rem">
							<strong>🎨 Current theme "{{ importDialogData.currentThemeName }}" updated:</strong><br />
							<span style="font-family: monospace; color: #059669; font-weight: 600">{{ importDialogData.currentTimestamp }}</span>
						</div>
						<div style="margin-bottom: 0.75rem">
							<strong>📁 Imported theme was updated:</strong><br />
							<span style="font-family: monospace; color: #dc2626; font-weight: 600">{{ importDialogData.importedTimestamp }}</span>
						</div>
						<div v-if="importDialogData.timeDifference > 0" style="background: #fed7d7; padding: 0.5rem; border-radius: 6px; margin-top: 0.5rem">
							<small style="color: #9b2c2c">
								⏱️ <strong>Time difference:</strong> Current theme is {{ importDialogData.timeDifference }} day(s) newer
							</small>
						</div>
					</div>

					<div style="background: #fef2f2; padding: 1rem; border-radius: 8px; border-left: 4px solid #dc2626">
						<p style="margin: 0; color: #7f1d1d">
							<strong>⚠️ Warning:</strong> Importing this older theme will overwrite all current theme settings including colors, images, and
							configurations. This action cannot be undone.
						</p>
					</div>
				</div>
			</div>
		</div>

		<template #footer>
			<Button label="Cancel Import" severity="secondary" @click="cancelImport" :disabled="isImporting" />
			<Button label="Import Anyway" severity="danger" @click="confirmImport" :loading="isImporting" icon="pi pi-exclamation-triangle" />
		</template>
	</Dialog>

	<!-- Apply Semantic Colors Dialog -->
	<Dialog
		header="Apply Semantic Colors to Theme"
		v-model:visible="showApplySemanticColorsDialog"
		:modal="true"
		:closable="true"
		:draggable="false"
		:style="{ width: '650px' }"
		class="apply-semantic-colors-dialog"
	>
		<div class="apply-semantic-colors-content">
			<div class="current-colors-display">
				<label class="field-label">Current Semantic Colors:</label>
				<div class="colors-preview">
					<div class="color-preview-item">
						<div class="color-swatch-small" :style="{ backgroundColor: colorValues['0'] }"></div>
						<span>Primary: {{ colorValues["0"] }}</span>
						<Button
							icon="pi pi-chevron-right"
							@click="showPrimaryGradations = !showPrimaryGradations"
							class="p-button-text p-button-sm gradation-button"
							v-tooltip="'Show primary color gradations'"
						/>
						<div v-if="showPrimaryGradations" class="gradations-overlay" style="z-index: 99999 !important">
							<div class="gradations-header">Primary Gradations</div>
							<div v-for="(color, index) in primaryGradations" :key="index" class="gradation-item">
								<div class="gradation-swatch" :style="{ backgroundColor: color }"></div>
								<span class="gradation-label">{{ index }}: {{ color }}</span>
							</div>
						</div>
					</div>
					<div class="color-preview-item">
						<div class="color-swatch-small" :style="{ backgroundColor: colorValues['1'] }"></div>
						<span>Secondary: {{ colorValues["1"] }}</span>
						<Button
							icon="pi pi-chevron-right"
							@click="showSecondaryGradations = !showSecondaryGradations"
							class="p-button-text p-button-sm gradation-button"
							v-tooltip="'Show secondary color gradations'"
						/>
						<div v-if="showSecondaryGradations" class="gradations-overlay">
							<div class="gradations-header">Secondary Gradations</div>
							<div v-for="(color, index) in secondaryGradations" :key="index" class="gradation-item">
								<div class="gradation-swatch" :style="{ backgroundColor: color }"></div>
								<span class="gradation-label">{{ index }}: {{ color }}</span>
							</div>
						</div>
					</div>
					<div class="color-preview-item">
						<div class="color-swatch-small" :style="{ backgroundColor: colorValues['2'] }"></div>
						<span>Accent: {{ colorValues["2"] }}</span>
						<Button
							icon="pi pi-chevron-right"
							@click="showAccentGradations = !showAccentGradations"
							class="p-button-text p-button-sm gradation-button"
							v-tooltip="'Show accent color gradations'"
						/>
						<div v-if="showAccentGradations" class="gradations-overlay">
							<div class="gradations-header">Accent Gradations</div>
							<div v-for="(color, index) in accentGradations" :key="index" class="gradation-item">
								<div class="gradation-swatch" :style="{ backgroundColor: color }"></div>
								<span class="gradation-label">{{ index }}: {{ color }}</span>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div class="field">
				<label for="applyStrategy" class="field-label">Choose application strategy:</label>
				<Select
					id="applyStrategy"
					v-model="selectedApplyStrategy"
					:options="applyStrategies"
					option-label="label"
					option-value="value"
					placeholder="Select a strategy"
					class="w-full"
				/>
			</div>

			<!-- Strategy explanation -->
			<div v-if="selectedApplyStrategy" class="strategy-explanation">
				<div class="explanation-card">
					<h4>{{ applyStrategies.find((s) => s.value === selectedApplyStrategy)?.label }}</h4>
					<p>{{ getApplyStrategyExplanation(selectedApplyStrategy) }}</p>
				</div>
			</div>
		</div>

		<template #footer>
			<Button label="Cancel" icon="pi pi-times" severity="secondary" @click="closeApplySemanticColorsDialog" />
			<Button label="Apply Colors" icon="pi pi-check" @click="applySemanticColorsToTheme" :disabled="!selectedApplyStrategy" />
		</template>
	</Dialog>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, nextTick, watch } from "vue"
import ColorPicker from "primevue/colorpicker"
import { useToast } from "primevue/usetoast"
import { useThemeManagerStore } from "@/store/themeManagerStore"
import Tabs from "primevue/tabs"
import TabList from "primevue/tablist"
import Tab from "primevue/tab"
import TabPanels from "primevue/tabpanels"
import TabPanel from "primevue/tabpanel"
import Checkbox from "primevue/checkbox"
import Button from "primevue/button"
import Select from "primevue/select"
import Dialog from "primevue/dialog"
import { TemplateUI, ThemeMgmClient, ThemeState, ThemeTemplate, Semantic, ColorSchemeMain, SeverityMain, APIException, FileParameter } from "../../../entities/NSwagGenerated"

// Helper function to safely convert objects to proper NSwag classes
const convertToNSwagClasses = (template: any) => {
	if (!template) return template

	try {
		// Convert the entire template using NSwag's fromJS method
		if (template.selectedTheme) {
			template.selectedTheme = ThemeTemplate.fromJS(template.selectedTheme)
		}

		// Double-check semantic conversion
		if (template.selectedTheme && template.selectedTheme.semantic) {
			const semantic = template.selectedTheme.semantic

			// If semantic doesn't have toJSON method, convert it
			if (typeof semantic.toJSON !== "function") {
				template.selectedTheme.semantic = Semantic.fromJS(semantic)
			}

			// Ensure colorScheme is properly converted
			if (semantic.colorScheme && typeof semantic.colorScheme.toJSON !== "function") {
				template.selectedTheme.semantic.colorScheme = ColorSchemeMain.fromJS(semantic.colorScheme)

				// Ensure light scheme is properly converted
				if (semantic.colorScheme.light && typeof semantic.colorScheme.light.toJSON !== "function") {
					template.selectedTheme.semantic.colorScheme.light = SeverityMain.fromJS(semantic.colorScheme.light)
				}
			}
		}

		return template
	} catch (error) {
		console.error("❌ Error converting to NSwag classes:", error)
		return template
	}
}
import InputText from "primevue/inputtext"
//SETTINGS
import systemConfigurationUILoginPage from "./systemConfigurationUILoginPage.vue"
import systemConfigurationUIColorsForComponents from "./systemConfigurationUIColorsForComponents.vue"
import systemConfigurationUIMainLayout from "./systemConfigurationUIMainLayout.vue"
// Create 12 numbered colors from 0 to 11 (3 named + 9 array)
const colorNumbers = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11"] as const
type ColorNumber = (typeof colorNumbers)[number]
let selectedThemeMainTab = ref<number>(0)
const themeMgmClient = new ThemeMgmClient(import.meta.env.VITE_API_URL?.toString())

// State for color selection
const selectedColorNumber = ref<string | null>(null) // Track the selected Color number for crosshair
const selectedColorForPickerNumber = ref<string | null>(null) // Track the Color number for picker
const selectedColor = ref<string | null>(null) // The selected Color code
const selectedColorForPicker = ref<string | null>(null) // The Color code for the picker
const isColorSelectionMode = ref(false)
const isLoadingTemplate = ref(false) // Flag to avoid conflicts during template loading

// Separate variables for named colors (connected to semantic colors)
const primaryColor = ref("#3B82F6") // Primary - Blue (semantic)
const secondaryColor = ref("#EF4444") // Secondary - Red (semantic)
const accentColor = ref("#6366F1") // Accent - Indigo (semantic)

// Array for additional colors (9 colors for custom palette)
const additionalColors = ref([
	"#F59E0B", // Orange (index 0, maps to color 3)
	"#10B981", // Green (index 1, maps to color 4)
	"#06B6D4", // Cyan (index 2, maps to color 5)
	"#84CC16", // Lime (index 3, maps to color 6)
	"#F97316", // Orange variant (index 4, maps to color 7)
	"#EC4899", // Pink (index 5, maps to color 8)
	"#8B5CF6", // Purple (index 6, maps to color 9)
	"#059669", // Emerald (index 7, new color 10)
	"#DC2626", // Rose (index 8, new color 11)
])

// Maintain backward compatibility with existing code
const colorValues = ref({
	"0": "#3B82F6",
	"1": "#EF4444",
	"2": "#6366F1",
	"3": "#F59E0B",
	"4": "#10B981",
	"5": "#06B6D4",
	"6": "#84CC16",
	"7": "#F97316",
	"8": "#EC4899",
	"9": "#8B5CF6",
	"10": "#059669",
	"11": "#DC2626",
} as Record<ColorNumber, string>)

// Simple theme history for undo functionality
const themeHistory = ref<any[]>([])
const hasColorChanges = ref(false)

// State to manage which color picker is visible
const showPicker = ref<string | null>(null)

// Quick color pickers state
const quickColorKeys = ["primary", "secondary", "accent"] as const
type QuickColorKey = (typeof quickColorKeys)[number]
const showQuickPicker = ref<QuickColorKey | null>(null)
const quickColorValues = ref<Record<QuickColorKey, string>>({
	primary: "#3B82F6",
	secondary: "#EF4444",
	accent: "#6366F1",
})

// State for hex input values for each color
const hexInputValues = ref<Record<ColorNumber, string>>({
	"0": "#3B82F6",
	"1": "#EF4444",
	"2": "#6366F1",
	"3": "#F59E0B",
	"4": "#10B981",
	"5": "#06B6D4",
	"6": "#84CC16",
	"7": "#F97316",
	"8": "#EC4899",
	"9": "#8B5CF6",
	"10": "#059669",
	"11": "#DC2626",
})

// Special labels for semantic colors (first 3)
const getColorLabel = (colorNumber: string): string => {
	switch (colorNumber) {
		case "0":
			return "Primary"
		case "1":
			return "Secondary"
		case "2":
			return "Accent"
		default:
			return ""
	}
}

// Check if color is a semantic color (first 3)
const isSemanticColor = (colorNumber: string): boolean => {
	return ["0", "1", "2"].includes(colorNumber)
}
let tabs = [
	{ tabName: "Login Page", tabPage: systemConfigurationUILoginPage, onlyForERMAdmin: true },
	{ tabName: "Main Layout", tabPage: systemConfigurationUIMainLayout, onlyForERMAdmin: true },
	{ tabName: "Various Components", tabPage: systemConfigurationUIColorsForComponents, onlyForERMAdmin: true },
]
const templates = ref<TemplateUI[]>([] as TemplateUI[])
const templateStore = ref<TemplateUI>(new TemplateUI())
const isDefaultPalette = ref<boolean>(true)
const selectedTemplateId = ref<string>()

// Variables for create theme dialog
const showCreateThemeDialog = ref(false)
const newThemeName = ref("")
const selectedBaseTheme = ref("")
const showValidationErrors = ref(false)

// Available base themes options
const availableBaseThemes = ref([{ label: "Default Theme", value: "default" }])

// Variables for standard theme warning
const isStandardTheme = ref(false)
const overrideStandardTheme = ref(false)

// Variables for color scheme selection dialog
const showColorSchemeDialog = ref(false)
const selectedColorSchemeType = ref("")
const baseColorForScheme = ref("")

// Variables for color scheme generator dialog
const showColorSchemeGeneratorDialog = ref(false)

// Variables for delete theme functionality
const showDeleteConfirmDialog = ref(false)
const selectedThemeToDelete = ref<TemplateUI | null>(null)
const isDeletingTheme = ref(false)

// Variables for import/export functionality
const showImportConfirmDialog = ref(false)
const importFileInput = ref<HTMLInputElement | null>(null)
const selectedImportFile = ref<File | null>(null)

// Variables for apply semantic colors dialog
const showApplySemanticColorsDialog = ref(false)
const selectedApplyStrategy = ref("")

// Variables for color gradations display
const showPrimaryGradations = ref(false)
const showSecondaryGradations = ref(false)
const showAccentGradations = ref(false)
const primaryGradations = ref<string[]>([])
const secondaryGradations = ref<string[]>([])
const accentGradations = ref<string[]>([])

// State for showing gradations overlays
const showGradations = ref<Record<string, boolean>>({
	"0": false, // primary
	"1": false, // secondary
	"2": false, // accent
})

// Available strategies for applying semantic colors
const applyStrategies = [
	{
		label: "Smart Theme Generation",
		value: "smart",
		description: "Intelligently applies colors throughout the theme based on color theory",
	},
	{
		label: "Component-Based Mapping",
		value: "components",
		description: "Maps colors to specific UI components (buttons, cards, etc.)",
	},
	{
		label: "Gradient Variations",
		value: "gradient",
		description: "Creates color variations from light to dark for each semantic color",
	},
	{
		label: "Monochromatic Scheme",
		value: "monochrome",
		description: "Uses variations of the primary color throughout the theme",
	},
	{
		label: "High Contrast Mode",
		value: "contrast",
		description: "Optimizes for accessibility with high contrast between elements",
	},
]
const isImporting = ref(false)
const importDialogData = ref({
	currentTimestamp: "",
	importedTimestamp: "",
	timeDifference: 0,
	currentThemeName: "",
	detailMessage: "",
})

const generatorBaseColor = ref("#3B82F6") // Default blue color
const showGeneratorColorPicker = ref(false)
const selectedGeneratorSchemeType = ref("")

// Variables for theme name editing
const isEditingThemeName = ref(false)
const editingThemeName = ref("")

// Available color scheme types based on color theory
const colorSchemeTypes = [
	{
		label: "Monochromatic (Shades of same hue)",
		value: "monochromatic",
		description: "Uses variations in lightness and saturation of a single color",
	},
	{ label: "Analogous (Adjacent colors)", value: "analogous", description: "Uses colors that are adjacent to each other on the color wheel" },
	{ label: "Complementary (Opposite colors)", value: "complementary", description: "Uses colors opposite each other on the color wheel" },
	{ label: "Triadic (Triangle)", value: "triadic", description: "Uses three colors evenly spaced around the color wheel" },
	{ label: "Split-Complementary", value: "splitComplementary", description: "Uses a base color and two colors adjacent to its complement" },
	{ label: "Tetradic (Rectangle)", value: "tetradic", description: "Uses four colors arranged into two complementary pairs" },
	{ label: "Professional Business", value: "business", description: "Conservative scheme suitable for business applications" },
	{ label: "Creative/Artistic", value: "creative", description: "Bold and vibrant scheme for creative applications" },
]

// Define emit function
const emit = defineEmits<{
	updateTheme: [themeData: ThemeState]
	colorSelected: [color: string]
}>()
const selectedTemplate = ref<ThemeState>(new ThemeState())
// References to tab components
const tabComponentRefs = ref<Record<number, any>>({})

const themeStore = useThemeManagerStore()
const toast = useToast()

// Function to set tab component references
const setTabComponentRef = (index: number, el: any) => {
	if (el) {
		tabComponentRefs.value[index] = el
	}
}

const updateThemeData = (themeData: ThemeState) => {
	selectedTemplate.value = convertToNSwagClasses(themeData)

	// Apply chip styles globally when theme is updated
	if (themeData.selectedTheme?.components) {
		themeStore.applyGlobalChipStyles(themeData.selectedTheme.components)
	}
}

const applyThemeToLive = async () => {
	try {
		if (!selectedTemplate.value || !selectedTemplateId.value) {
			toast.add({
				severity: "warn",
				summary: "No Theme Selected",
				detail: "Please select a theme to apply",
				life: 3000,
			})
			return
		}

		// Apply the theme to the live application
		await themeStore.loadSingleCompleteTemplate(selectedTemplateId.value, "live")

		toast.add({
			severity: "success",
			summary: "Theme Applied",
			detail: "Theme has been applied to the live application",
			life: 3000,
		})
	} catch (error) {
		console.error("Error applying theme to live:", error)
		toast.add({
			severity: "error",
			summary: "Apply Error",
			detail: "Failed to apply theme to live application",
			life: 5000,
		})
	}
}

const saveTheme = async () => {
	try {
		// Ensure selectedTheme and semantic objects exist
		if (!selectedTemplate.value.selectedTheme) {
			selectedTemplate.value.selectedTheme = new ThemeTemplate()
		}
		if (!selectedTemplate.value.selectedTheme.semantic) {
			selectedTemplate.value.selectedTheme.semantic = new Semantic()
		}

		// Colors will be saved to colorScheme.light structure below

		// Save the remaining colors (3-11) in customPalette
		const customPaletteColors: any = {}
		for (let i = 3; i <= 11; i++) {
			customPaletteColors[i.toString()] = colorValues.value[i.toString() as ColorNumber]
		}
		selectedTemplate.value.selectedTheme.semantic!.customPalette = customPaletteColors

		// Generate color gradations for primary, secondary, and accent
		const primaryColor = colorValues.value["0"]
		const secondaryColor = colorValues.value["1"]
		const accentColor = colorValues.value["2"]

		// Generate 10 gradations for each color
		const primaryGradations = generateColorScale(primaryColor)
		const secondaryGradations = generateColorScale(secondaryColor)
		const accentGradations = generateColorScale(accentColor)

		// Ensure colorScheme structure exists using proper NSwag constructors
		const semantic = selectedTemplate.value.selectedTheme.semantic
		if (!semantic.colorScheme) {
			semantic.colorScheme = new ColorSchemeMain({
				light: new SeverityMain({
					primary: colorValues.value["0"],
					secondary: colorValues.value["1"],
					accent: colorValues.value["2"],
					primaryGradations: {},
					secondaryGradations: {},
					accentGradations: {},
				}),
			})
		}

		// Ensure light mode structure exists
		if (!semantic.colorScheme.light) {
			semantic.colorScheme.light = new SeverityMain({
				primary: colorValues.value["0"],
				secondary: colorValues.value["1"],
				accent: colorValues.value["2"],
				primaryGradations: {},
				secondaryGradations: {},
				accentGradations: {},
			})
		}

		// Save base colors to colorScheme.light (maintain string structure for API compatibility)
		semantic.colorScheme.light.primary = colorValues.value["0"]
		semantic.colorScheme.light.secondary = colorValues.value["1"]
		semantic.colorScheme.light.accent = colorValues.value["2"]

		// Save gradations to separate properties
		if (!semantic.colorScheme.light.primaryGradations) semantic.colorScheme.light.primaryGradations = {}
		if (!semantic.colorScheme.light.secondaryGradations) semantic.colorScheme.light.secondaryGradations = {}
		if (!semantic.colorScheme.light.accentGradations) semantic.colorScheme.light.accentGradations = {}

		for (let i = 0; i < 10; i++) {
			semantic.colorScheme.light.primaryGradations[i.toString()] = primaryGradations[i]
			semantic.colorScheme.light.secondaryGradations[i.toString()] = secondaryGradations[i]
			semantic.colorScheme.light.accentGradations[i.toString()] = accentGradations[i]
		}

		// Save any pending images from the Login Page tab (index 0)
		const loginPageComponent = tabComponentRefs.value[0]
		if (loginPageComponent && loginPageComponent.savePendingImages) {
			await loginPageComponent.savePendingImages()
		}

		// Save font settings from Various Components tab (index 2) to JSON
		const variousComponentsTab = tabComponentRefs.value[2]
		if (variousComponentsTab && variousComponentsTab.getCurrentFontSettings) {
			try {
				const fontSettings = variousComponentsTab.getCurrentFontSettings()
				if (fontSettings && selectedTemplate.value.templateUI) {
					// Get current themeSettings or create empty object
					const currentThemeSettings = selectedTemplate.value.templateUI.themeSettings
						? JSON.parse(selectedTemplate.value.templateUI.themeSettings)
						: {}

					// Add font settings to JSON
					currentThemeSettings.fontSettings = {
						fontFamily: fontSettings.fontFamily,
						fontSize: fontSettings.fontSize,
						fontWeight: fontSettings.fontWeight,
						lineHeight: fontSettings.lineHeight,
						letterSpacing: fontSettings.letterSpacing,
						fontStyle: fontSettings.fontStyle,
						textDecoration: fontSettings.textDecoration,
						textTransform: fontSettings.textTransform,
						color: fontSettings.color,
						isDefault: fontSettings.isDefault,
					}

					// Save back to themeSettings
					selectedTemplate.value.templateUI.themeSettings = JSON.stringify(currentThemeSettings)

					// Also attach to selectedTheme.fontSettings for consistency
					if (selectedTemplate.value.selectedTheme) {
						// Create object with toJSON method for API compatibility
						const fontSettingsObj = {
							id: "json-based-id",
							themeId: selectedTemplate.value.templateUI.id || "",
							fontFamily: fontSettings.fontFamily,
							fontSize: fontSettings.fontSize,
							fontWeight: fontSettings.fontWeight,
							lineHeight: fontSettings.lineHeight,
							letterSpacing: fontSettings.letterSpacing,
							fontStyle: fontSettings.fontStyle,
							textDecoration: fontSettings.textDecoration,
							textTransform: fontSettings.textTransform,
							color: fontSettings.color,
							isDefault: fontSettings.isDefault,
							// Add toJSON method for API compatibility
							toJSON: function () {
								const { toJSON, ...rest } = this
								return rest
							},
							init: function () {
								return this
							},
						}
						selectedTemplate.value.selectedTheme.fontSettings = fontSettingsObj
					}
				}
			} catch (error) {
				console.error("❌ Error saving font settings to JSON:", error)
				// Don't fail the entire save process if font settings fail
			}
		}

		// Save the complete theme
		try {
			// Debug: Log the object structure before save
			console.log("🔍 About to save template:", selectedTemplate.value)
			console.log("🔍 ColorScheme object:", selectedTemplate.value.selectedTheme?.semantic?.colorScheme)
			console.log("🔍 ColorScheme has toJSON?", typeof selectedTemplate.value.selectedTheme?.semantic?.colorScheme?.toJSON === "function")
			console.log("🔍 Light object:", selectedTemplate.value.selectedTheme?.semantic?.colorScheme?.light)
			console.log("🔍 Light has toJSON?", typeof selectedTemplate.value.selectedTheme?.semantic?.colorScheme?.light?.toJSON === "function")
			console.log("🍞 Breadcrumb colors before save:", (selectedTemplate.value.selectedTheme?.semantic as any)?.breadcrumb)

			// Ensure complete conversion before save
			selectedTemplate.value = convertToNSwagClasses(selectedTemplate.value)
			console.log("🍞 Breadcrumb colors after conversion:", (selectedTemplate.value.selectedTheme?.semantic as any)?.breadcrumb)

			// Pass "configuration" context to prevent updating the live theme (logo, etc.) in frame.vue
			const saveResult = await themeStore.saveSingleTemplate(selectedTemplate.value, "configuration")
		} catch (error) {
			console.error("❌ Error during theme save:", error)
			throw error
		}

		// Show success toast
		toast.add({
			severity: "success",
			summary: "Save Completed",
			detail: "Theme has been saved successfully",
			life: 3000,
		})
	} catch (error) {
		console.error("Error saving theme:", error)

		// Show error toast
		toast.add({
			severity: "error",
			summary: "Save Error",
			detail: "An error occurred while saving the theme: " + (error as any).message,
			life: 5000,
		})
	}
}

const getDefaultTemplateId = async (): Promise<string | null> => {
	if (templates.value.length > 0) {
		var defaultTemplate = templates.value.find((t) => t.isDefaultTemplate)
		if (defaultTemplate && defaultTemplate.id) {
			selectedTemplateId.value = defaultTemplate.id
			return defaultTemplate.id
		}
	}
	return null
}

const loadTemplatesFromDB = async (preserveSelection = false, preservedId?: string) => {
	try {
		const res = await themeMgmClient.getAllThemes()
		// Convert all templates to proper NSwag classes
		templates.value = res.map(convertToNSwagClasses)

		// If we should preserve the selection, use the preserved ID
		if (preserveSelection && preservedId) {
			// Check if the preserved theme still exists in the list
			const themeStillExists = res.some((t) => t.id === preservedId)
			if (themeStillExists) {
				selectedTemplateId.value = preservedId
				await loadTemplateFromDB()
				return
			}
		}

		// Otherwise, load the default template
		var defaultTemplateId = await getDefaultTemplateId()
		if (defaultTemplateId) {
			selectedTemplateId.value = defaultTemplateId
			await loadTemplateFromDB()
		}
	} catch (err) {
		throw new Error(`Error from API: ${err}`)
	}
}
const loadTemplateFromDB = async () => {
	try {
		if (!selectedTemplateId.value) {
			return
		}

		// Set loading flag to block color selections

		isLoadingTemplate.value = true

		var completeTemp = await themeStore.loadSingleCompleteTemplate(selectedTemplateId.value, "configuration")
		console.log("🍞 Breadcrumb colors loaded from DB:", (completeTemp?.selectedTheme?.semantic as any)?.breadcrumb)
		selectedTemplate.value = completeTemp!
		templateStore.value = completeTemp?.templateUI ?? new TemplateUI()

		// Check if this is a standard theme
		isStandardTheme.value = completeTemp?.templateUI?.isStandardTheme || false
		overrideStandardTheme.value = false // Reset override when loading a new theme

		nextTick(() => {
			resetThemeHistory().then(async () => {
				// Load font settings for the Various Components tab after template loads
				const variousComponentsTab = tabComponentRefs.value[2]
				if (variousComponentsTab && variousComponentsTab.loadFontSettingsFromTheme) {
					try {
						await variousComponentsTab.loadFontSettingsFromTheme()
					} catch (error) {
						console.error("❌ Error loading font settings for new theme:", error)
					}
				}
			})
		})

		// Load custom palette if it exists
		await loadCustomPalette(completeTemp)
		// Reset theme history when changing themes

		// Reset loading flag

		isLoadingTemplate.value = false
	} catch (err) {
		isLoadingTemplate.value = false // Reset flag even in case of error
		throw new Error(`Error from API: ${err}`)
	}
}

// Function to load colors from the current theme without reloading from DB
const loadColorsFromCurrentTheme = async () => {
	// Use the existing loadCustomPalette function with current theme
	await loadCustomPalette(selectedTemplate.value)
}

// Function to select a Color with left click
const selectColor = (color: string, colorNumber: string) => {
	selectedColorNumber.value = colorNumber
	selectedColor.value = color
	isColorSelectionMode.value = true

	// Change mouse cursor to crosshair
	document.body.style.cursor = "crosshair"

	// Do NOT regenerate the palette - the Color is only selected for use
}
// Function to disable a Color with left click
const disablePasteColor = (_color: string, _colorNumber: string) => {
	selectedColorNumber.value = null
	selectedColor.value = null
	isColorSelectionMode.value = false

	// Change mouse cursor to default
	document.body.style.cursor = "default"

	// Do NOT regenerate the palette - the Color is only selected for use
}

// Function to show/hide the color picker with button
const toggleColorPicker = (colorNumber: ColorNumber) => {
	const wasOpen = showPicker.value === colorNumber

	if (wasOpen) {
		// ColorPicker is being closed - check if colors have changed and save previous state to history
		// Color picker closed - save theme to history if changes occurred
		saveThemeToHistory()

		// Close the picker
		showPicker.value = null
		selectedColorForPickerNumber.value = null
		selectedColorForPicker.value = null
	} else {
		// ColorPicker is being opened - save current state (both sidebar colors and semantic state)
		let currentSemanticState = null
		if (selectedTemplate.value.selectedTheme?.semantic) {
			currentSemanticState = JSON.parse(JSON.stringify(selectedTemplate.value.selectedTheme.semantic))
		}

		// Save current theme state before opening picker
		themeBeforePickerOpen = selectedTemplate.value.selectedTheme ? JSON.parse(JSON.stringify(selectedTemplate.value.selectedTheme)) : null

		// Open the picker
		showPicker.value = colorNumber
		selectedColorForPickerNumber.value = colorNumber
		selectedColorForPicker.value = colorValues.value[colorNumber]
		// Synchronize the hex input value
		hexInputValues.value[colorNumber] = colorValues.value[colorNumber]
	}
}

// Function to hide the color picker when clicking elsewhere
const hideColorPicker = () => {
	// Color picker closed - save theme to history if changes occurred
	saveThemeToHistory()
	showPicker.value = null
	selectedColorForPickerNumber.value = null
	selectedColorForPicker.value = null
}

// Quick color picker functions
const getQuickColorValue = (colorKey: QuickColorKey): string => {
	// Map quick colors to the corresponding numbered colors in the palette
	const colorMapping: Record<QuickColorKey, ColorNumber> = {
		primary: "0",
		secondary: "1",
		accent: "2",
	}
	return colorValues.value[colorMapping[colorKey]]
}

const toggleQuickColorPicker = (colorKey: QuickColorKey) => {
	showQuickPicker.value = showQuickPicker.value === colorKey ? null : colorKey

	if (showQuickPicker.value === colorKey) {
		// Sync the quick color value with the palette value
		quickColorValues.value[colorKey] = getQuickColorValue(colorKey)
	}
}

const updateQuickColor = (colorKey: QuickColorKey, newColor: any) => {
	const hexColor = typeof newColor === "string" ? newColor : newColor?.value || newColor
	const formattedColor = hexColor?.startsWith?.("#") ? hexColor : `#${hexColor}`

	// Update the quick color value
	quickColorValues.value[colorKey] = formattedColor

	// Update the corresponding numbered color in the main palette
	const colorMapping: Record<QuickColorKey, ColorNumber> = {
		primary: "0",
		secondary: "1",
		accent: "2",
	}

	const colorNumber = colorMapping[colorKey] as ColorNumber
	colorValues.value[colorNumber] = formattedColor
	if (hexInputValues.value) {
		hexInputValues.value[colorNumber] = formattedColor
	}

	// Check if palette matches defaults
	checkIfDefaultPalette()
}

// Function to toggle gradations overlay
const toggleGradations = (event: MouseEvent, colorNumber: string) => {
	// Close all other gradation overlays and remove any existing modal
	Object.keys(showGradations.value).forEach((key) => {
		showGradations.value[key] = false
	})

	// Remove any existing gradations modal
	const existingModal = document.getElementById("gradations-modal")
	if (existingModal) {
		existingModal.remove()
	}

	// Toggle the selected gradations overlay
	if (!showGradations.value[colorNumber]) {
		showGradations.value[colorNumber] = true

		// Get button position
		const button = event.target as HTMLElement
		const buttonRect = button.getBoundingClientRect()
		const modalWidth = 320
		const modalHeight = 400

		// Calculate position - prefer right side, but use left if no space
		const spaceOnRight = window.innerWidth - buttonRect.right
		const spaceOnLeft = buttonRect.left

		let left: number
		let top: number

		if (spaceOnRight >= modalWidth + 8) {
			// Position to the right
			left = buttonRect.right + 8
		} else if (spaceOnLeft >= modalWidth + 8) {
			// Position to the left
			left = buttonRect.left - modalWidth - 8
		} else {
			// Center horizontally if no space on either side
			left = Math.max(8, (window.innerWidth - modalWidth) / 2)
		}

		// Ensure modal doesn't go below viewport
		if (buttonRect.top + modalHeight > window.innerHeight) {
			top = Math.max(8, window.innerHeight - modalHeight - 8)
		} else {
			top = buttonRect.top
		}

		// Create modal positioned next to button
		const modal = document.createElement("div")
		modal.id = "gradations-modal"
		modal.style.cssText = `
			position: fixed !important;
			top: ${top}px !important;
			left: ${left}px !important;
			z-index: 2147483647 !important;
		`

		const content = document.createElement("div")
		content.style.cssText = `
			background: white !important;
			border: 1px solid #e5e7eb !important;
			border-radius: 8px !important;
			padding: 16px !important;
			width: 320px !important;
			max-height: 400px !important;
			overflow-y: auto !important;
			box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
		`

		const title = document.createElement("h4")
		title.textContent = getColorLabel(colorNumber) + " Gradations"
		title.style.cssText = "margin: 0 0 12px 0 !important; color: #374151 !important; font-size: 1.25rem !important; font-weight: 600 !important;"

		// Create a grid container for gradations
		const gridContainer = document.createElement("div")
		gridContainer.style.cssText = `
			display: grid !important;
			grid-template-columns: 1fr 1fr !important;
			gap: 8px !important;
		`

		const gradations = getGradationsForColor(colorNumber)
		gradations.forEach((gradation, index) => {
			const item = document.createElement("div")
			item.style.cssText = `
				display: flex !important;
				align-items: center !important;
				gap: 8px !important;
				margin-bottom: 4px !important;
			`

			const swatch = document.createElement("div")
			swatch.style.cssText = `
				width: 20px !important;
				height: 20px !important;
				background-color: ${gradation} !important;
				border-radius: 4px !important;
				border: 1px solid #e5e7eb !important;
				flex-shrink: 0 !important;
			`

			const label = document.createElement("span")
			label.textContent = `${index}: ${gradation}`
			label.style.cssText = "color: #374151 !important; font-size: 0.875rem !important; font-family: monospace !important;"

			item.appendChild(swatch)
			item.appendChild(label)
			gridContainer.appendChild(item)
		})

		content.appendChild(gridContainer)

		content.insertBefore(title, content.firstChild)
		modal.appendChild(content)
		document.body.appendChild(modal)

		// Close on click outside - add global click listener
		const handleClickOutside = (e: Event) => {
			const clickedElement = e.target as HTMLElement
			if (!modal.contains(clickedElement)) {
				modal.remove()
				showGradations.value[colorNumber] = false
				document.removeEventListener("click", handleClickOutside)
			}
		}

		// Delay adding the listener to avoid immediate closure
		setTimeout(() => {
			document.addEventListener("click", handleClickOutside)
		}, 100)
	}
}

// Function to get gradations for a specific color
const getGradationsForColor = (colorNumber: string) => {
	switch (colorNumber) {
		case "0": // primary
			return primaryGradations.value.length > 0 ? primaryGradations.value : generateColorScale(colorValues.value["0"])
		case "1": // secondary
			return secondaryGradations.value.length > 0 ? secondaryGradations.value : generateColorScale(colorValues.value["1"])
		case "2": // accent
			return accentGradations.value.length > 0 ? accentGradations.value : generateColorScale(colorValues.value["2"])
		default:
			return []
	}
}

// Function to show gradient picker for color pickers
const showGradientPicker = (event: MouseEvent, colorNumber: string) => {
	// Remove any existing gradient picker modal
	const existingModal = document.getElementById("gradient-picker-modal")
	if (existingModal) {
		existingModal.remove()
	}

	// Get button position
	const button = event.target as HTMLElement
	const buttonRect = button.getBoundingClientRect()
	const modalWidth = 320
	const modalHeight = 400

	// Calculate position - prefer right side, but use left if no space
	const spaceOnRight = window.innerWidth - buttonRect.right
	const spaceOnLeft = buttonRect.left

	let left: number
	let top: number

	if (spaceOnRight >= modalWidth + 8) {
		// Position to the right
		left = buttonRect.right + 8
	} else if (spaceOnLeft >= modalWidth + 8) {
		// Position to the left
		left = buttonRect.left - modalWidth - 8
	} else {
		// Center horizontally if no space on either side
		left = Math.max(8, (window.innerWidth - modalWidth) / 2)
	}

	// Ensure modal doesn't go below viewport
	if (buttonRect.top + modalHeight > window.innerHeight) {
		top = Math.max(8, window.innerHeight - modalHeight - 8)
	} else {
		top = buttonRect.top
	}

	// Create modal positioned next to button
	const modal = document.createElement("div")
	modal.id = "gradient-picker-modal"
	modal.style.cssText = `
		position: fixed !important;
		top: ${top}px !important;
		left: ${left}px !important;
		z-index: 2147483647 !important;
	`

	const content = document.createElement("div")
	content.style.cssText = `
		background: white !important;
		border: 1px solid #e5e7eb !important;
		border-radius: 8px !important;
		padding: 16px !important;
		width: 320px !important;
		max-height: 400px !important;
		overflow-y: auto !important;
		box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
	`

	const title = document.createElement("h4")
	title.textContent = "Select Color from Gradients"
	title.style.cssText = "margin: 0 0 12px 0 !important; color: #374151 !important; font-size: 1.25rem !important; font-weight: 600 !important;"

	// Create gradient sections for primary, secondary, and accent
	const gradientTypes = [
		{ label: "Primary", gradations: primaryGradations.value.length > 0 ? primaryGradations.value : generateColorScale(colorValues.value["0"]) },
		{ label: "Secondary", gradations: secondaryGradations.value.length > 0 ? secondaryGradations.value : generateColorScale(colorValues.value["1"]) },
		{ label: "Accent", gradations: accentGradations.value.length > 0 ? accentGradations.value : generateColorScale(colorValues.value["2"]) },
	]

	gradientTypes.forEach((gradientType) => {
		const sectionTitle = document.createElement("h5")
		sectionTitle.textContent = gradientType.label
		sectionTitle.style.cssText =
			"margin: 12px 0 8px 0 !important; color: #6B7280 !important; font-size: 0.875rem !important; font-weight: 600 !important; text-transform: uppercase !important;"
		content.appendChild(sectionTitle)

		// Create a grid container for this gradient type
		const gridContainer = document.createElement("div")
		gridContainer.style.cssText = `
			display: grid !important;
			grid-template-columns: 1fr 1fr !important;
			gap: 6px !important;
			margin-bottom: 16px !important;
		`

		gradientType.gradations.forEach((gradation, index) => {
			const item = document.createElement("div")
			item.style.cssText = `
				display: flex !important;
				align-items: center !important;
				gap: 8px !important;
				padding: 4px 8px !important;
				border-radius: 4px !important;
				cursor: pointer !important;
				transition: background-color 0.2s ease !important;
			`

			// Add hover effect
			item.onmouseenter = () => {
				item.style.backgroundColor = "#f9fafb !important"
			}
			item.onmouseleave = () => {
				item.style.backgroundColor = "transparent !important"
			}

			// Add click handler to select color
			item.onclick = () => {
				updateColorValue(colorNumber as ColorNumber, { value: gradation })
				modal.remove()
			}

			const swatch = document.createElement("div")
			swatch.style.cssText = `
				width: 20px !important;
				height: 20px !important;
				background-color: ${gradation} !important;
				border-radius: 4px !important;
				border: 1px solid #e5e7eb !important;
				flex-shrink: 0 !important;
			`

			const label = document.createElement("span")
			label.textContent = `${index}: ${gradation}`
			label.style.cssText = "color: #374151 !important; font-size: 0.75rem !important; font-family: monospace !important;"

			item.appendChild(swatch)
			item.appendChild(label)
			gridContainer.appendChild(item)
		})

		content.appendChild(gridContainer)
	})

	content.insertBefore(title, content.firstChild)
	modal.appendChild(content)
	document.body.appendChild(modal)

	// Close on click outside - add global click listener
	const handleClickOutside = (e: Event) => {
		const clickedElement = e.target as HTMLElement
		if (!modal.contains(clickedElement)) {
			modal.remove()
			document.removeEventListener("click", handleClickOutside)
		}
	}

	// Delay adding the listener to avoid immediate closure
	setTimeout(() => {
		document.addEventListener("click", handleClickOutside)
	}, 100)
}

// Function to show gradient picker for quick color pickers
const showQuickGradientPicker = (event: MouseEvent, colorKey: string) => {
	// Remove any existing gradient picker modal
	const existingModal = document.getElementById("quick-gradient-picker-modal")
	if (existingModal) {
		existingModal.remove()
	}

	// Get button position
	const button = event.target as HTMLElement
	const buttonRect = button.getBoundingClientRect()
	const modalWidth = 320
	const modalHeight = 400

	// Calculate position - prefer right side, but use left if no space
	const spaceOnRight = window.innerWidth - buttonRect.right
	const spaceOnLeft = buttonRect.left

	let left: number
	let top: number

	if (spaceOnRight >= modalWidth + 8) {
		// Position to the right
		left = buttonRect.right + 8
	} else if (spaceOnLeft >= modalWidth + 8) {
		// Position to the left
		left = buttonRect.left - modalWidth - 8
	} else {
		// Center horizontally if no space on either side
		left = Math.max(8, (window.innerWidth - modalWidth) / 2)
	}

	// Ensure modal doesn't go below viewport
	if (buttonRect.top + modalHeight > window.innerHeight) {
		top = Math.max(8, window.innerHeight - modalHeight - 8)
	} else {
		top = buttonRect.top
	}

	// Create modal positioned next to button
	const modal = document.createElement("div")
	modal.id = "quick-gradient-picker-modal"
	modal.style.cssText = `
		position: fixed !important;
		top: ${top}px !important;
		left: ${left}px !important;
		z-index: 2147483647 !important;
	`

	const content = document.createElement("div")
	content.style.cssText = `
		background: white !important;
		border: 1px solid #e5e7eb !important;
		border-radius: 8px !important;
		padding: 16px !important;
		width: 320px !important;
		max-height: 400px !important;
		overflow-y: auto !important;
		box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
	`

	const title = document.createElement("h4")
	title.textContent = "Select Color from Gradients"
	title.style.cssText = "margin: 0 0 12px 0 !important; color: #374151 !important; font-size: 1.25rem !important; font-weight: 600 !important;"

	// Create gradient sections for primary, secondary, and accent
	const gradientTypes = [
		{ label: "Primary", gradations: primaryGradations.value.length > 0 ? primaryGradations.value : generateColorScale(colorValues.value["0"]) },
		{ label: "Secondary", gradations: secondaryGradations.value.length > 0 ? secondaryGradations.value : generateColorScale(colorValues.value["1"]) },
		{ label: "Accent", gradations: accentGradations.value.length > 0 ? accentGradations.value : generateColorScale(colorValues.value["2"]) },
	]

	gradientTypes.forEach((gradientType) => {
		const sectionTitle = document.createElement("h5")
		sectionTitle.textContent = gradientType.label
		sectionTitle.style.cssText =
			"margin: 12px 0 8px 0 !important; color: #6B7280 !important; font-size: 0.875rem !important; font-weight: 600 !important; text-transform: uppercase !important;"
		content.appendChild(sectionTitle)

		// Create a grid container for this gradient type
		const gridContainer = document.createElement("div")
		gridContainer.style.cssText = `
			display: grid !important;
			grid-template-columns: 1fr 1fr !important;
			gap: 6px !important;
			margin-bottom: 16px !important;
		`

		gradientType.gradations.forEach((gradation, index) => {
			const item = document.createElement("div")
			item.style.cssText = `
				display: flex !important;
				align-items: center !important;
				gap: 8px !important;
				padding: 4px 8px !important;
				border-radius: 4px !important;
				cursor: pointer !important;
				transition: background-color 0.2s ease !important;
			`

			// Add hover effect
			item.onmouseenter = () => {
				item.style.backgroundColor = "#f9fafb !important"
			}
			item.onmouseleave = () => {
				item.style.backgroundColor = "transparent !important"
			}

			// Add click handler to select color
			item.onclick = () => {
				// Find the color number mapping for the quick color key
				const colorMapping = { primary: "0", secondary: "1", accent: "2" }
				const mappedColorNumber = colorMapping[colorKey as keyof typeof colorMapping]
				if (mappedColorNumber) {
					updateColorValue(mappedColorNumber as ColorNumber, { value: gradation })
				}
				modal.remove()
				showQuickPicker.value = null // Close the quick picker
			}

			const swatch = document.createElement("div")
			swatch.style.cssText = `
				width: 20px !important;
				height: 20px !important;
				background-color: ${gradation} !important;
				border-radius: 4px !important;
				border: 1px solid #e5e7eb !important;
				flex-shrink: 0 !important;
			`

			const label = document.createElement("span")
			label.textContent = `${index}: ${gradation}`
			label.style.cssText = "color: #374151 !important; font-size: 0.75rem !important; font-family: monospace !important;"

			item.appendChild(swatch)
			item.appendChild(label)
			gridContainer.appendChild(item)
		})

		content.appendChild(gridContainer)
	})

	content.insertBefore(title, content.firstChild)
	modal.appendChild(content)
	document.body.appendChild(modal)

	// Close on click outside - add global click listener
	const handleClickOutside = (e: Event) => {
		const clickedElement = e.target as HTMLElement
		if (!modal.contains(clickedElement)) {
			modal.remove()
			document.removeEventListener("click", handleClickOutside)
		}
	}

	// Delay adding the listener to avoid immediate closure
	setTimeout(() => {
		document.addEventListener("click", handleClickOutside)
	}, 100)
}

// Function to exit Color selection mode
const exitColorSelectionMode = () => {
	selectedColorNumber.value = null
	selectedColor.value = null
	selectedColorForPickerNumber.value = null
	selectedColorForPicker.value = null
	isColorSelectionMode.value = false
	document.body.style.cursor = "default"
}

// Simplified function to load palette with forced reactivity
const loadCustomPalette = async (template: any) => {
	// Create a new object to force reactivity
	const newColorValues: Record<ColorNumber, string> = {} as Record<ColorNumber, string>
	const newHexInputValues: Record<ColorNumber, string> = {} as Record<ColorNumber, string>

	// Always ensure semantic object exists and initialize semantic colors
	if (template?.selectedTheme) {
		// Ensure semantic object exists
		if (!template.selectedTheme.semantic) {
			template.selectedTheme.semantic = new Semantic()
		}

		// Ensure colorScheme structure exists
		if (!template.selectedTheme.semantic.colorScheme) {
			template.selectedTheme.semantic.colorScheme = new ColorSchemeMain({
				light: new SeverityMain({
					primary: defaultColors["0"],
					secondary: defaultColors["1"],
					accent: defaultColors["2"],
					primaryGradations: {},
					secondaryGradations: {},
					accentGradations: {},
				}),
			})
		}
		if (!template.selectedTheme.semantic.colorScheme.light) {
			template.selectedTheme.semantic.colorScheme.light = new SeverityMain({
				primary: defaultColors["0"],
				secondary: defaultColors["1"],
				accent: defaultColors["2"],
				primaryGradations: {},
				secondaryGradations: {},
				accentGradations: {},
			})
		}

		// Load or initialize semantic colors (0, 1, 2)
		// Initialize as strings according to NSwag types (SeverityMain expects string values)
		if (!template.selectedTheme.semantic.colorScheme.light.primary) {
			template.selectedTheme.semantic.colorScheme.light.primary = defaultColors["0"]
		}
		if (!template.selectedTheme.semantic.colorScheme.light.secondary) {
			template.selectedTheme.semantic.colorScheme.light.secondary = defaultColors["1"]
		}
		if (!template.selectedTheme.semantic.colorScheme.light.accent) {
			template.selectedTheme.semantic.colorScheme.light.accent = defaultColors["2"]
		}

		// Support both old and new color structure
		const getPrimaryColor = () => {
			const primary = template.selectedTheme.semantic.colorScheme.light.primary
			return primary && typeof primary === "object" && primary.color ? primary.color : primary || defaultColors["0"]
		}
		const getSecondaryColor = () => {
			const secondary = template.selectedTheme.semantic.colorScheme.light.secondary
			return secondary && typeof secondary === "object" && secondary.color ? secondary.color : secondary || defaultColors["1"]
		}
		const getAccentColor = () => {
			const accent = template.selectedTheme.semantic.colorScheme.light.accent
			return accent && typeof accent === "object" && accent.color ? accent.color : accent || defaultColors["2"]
		}

		newColorValues["0"] = getPrimaryColor()
		newColorValues["1"] = getSecondaryColor()
		newColorValues["2"] = getAccentColor()
		newHexInputValues["0"] = newColorValues["0"]
		newHexInputValues["1"] = newColorValues["1"]
		newHexInputValues["2"] = newColorValues["2"]

		// Load custom palette colors (3-11) if available
		if (template.selectedTheme.semantic && template.selectedTheme.semantic.customPalette) {
			const savedPalette = template.selectedTheme.semantic.customPalette

			// Apply saved custom colors or use defaults for colors 3-11
			for (let i = 3; i <= 11; i++) {
				const colorKey = i.toString() as ColorNumber
				if (savedPalette[colorKey]) {
					newColorValues[colorKey] = savedPalette[colorKey]
					newHexInputValues[colorKey] = savedPalette[colorKey]
				} else {
					newColorValues[colorKey] = defaultColors[colorKey]
					newHexInputValues[colorKey] = defaultColors[colorKey]
				}
			}
		} else {
			// Use defaults for colors 3-11
			for (let i = 3; i <= 11; i++) {
				const colorKey = i.toString() as ColorNumber
				newColorValues[colorKey] = defaultColors[colorKey]
				newHexInputValues[colorKey] = defaultColors[colorKey]
			}
		}

		isDefaultPalette.value = false
	} else {
		// No semantic colors available, use defaults for everything
		isDefaultPalette.value = true

		colorNumbers.forEach((colorNumber) => {
			newColorValues[colorNumber] = defaultColors[colorNumber]
			newHexInputValues[colorNumber] = defaultColors[colorNumber]
		})
	}

	// Wait for next tick to ensure reactivity is activated
	await nextTick()

	// Force reactive update by assigning all values together
	colorValues.value = { ...newColorValues }
	Object.assign(hexInputValues.value, newHexInputValues)

	// Wait for next tick to ensure reactivity is activated
	await nextTick()

	// Load gradations if they exist in the theme
	if (template?.selectedTheme?.semantic?.colorScheme?.light) {
		const lightColors = template.selectedTheme.semantic.colorScheme.light

		// Load primary gradations
		if (lightColors.primary) {
			const primaryColors: string[] = []
			for (let i = 0; i < 10; i++) {
				const color = lightColors.primary[i.toString()]
				if (color) {
					primaryColors.push(color)
				}
			}
			if (primaryColors.length === 10) {
				primaryGradations.value = primaryColors
			}
		}

		// Load secondary gradations
		if (lightColors.secondary) {
			const secondaryColors: string[] = []
			for (let i = 0; i < 10; i++) {
				const color = lightColors.secondary[i.toString()]
				if (color) {
					secondaryColors.push(color)
				}
			}
			if (secondaryColors.length === 10) {
				secondaryGradations.value = secondaryColors
			}
		}

		// Load accent gradations
		if (lightColors.accent) {
			const accentColors: string[] = []
			for (let i = 0; i < 10; i++) {
				const color = lightColors.accent[i.toString()]
				if (color) {
					accentColors.push(color)
				}
			}
			if (accentColors.length === 10) {
				accentGradations.value = accentColors
			}
		}
	}

	// Check if it matches defaults
	checkIfDefaultPalette()
}

// Function for manual reset of palette to default values
const resetToDefaultPalette = () => {
	// Save current theme before resetting
	// Note: Reset doesn't need to save to history as it's intentional

	resetToDefaultColors()
	isDefaultPalette.value = true
}

// Constant with default colors
const defaultColors: Record<ColorNumber, string> = {
	"0": "#3B82F6", // Primary - Blue (semantic)
	"1": "#EF4444", // Secondary - Red (semantic)
	"2": "#6366F1", // Accent - Indigo (semantic)
	"3": "#F59E0B", // Orange
	"4": "#10B981", // Green
	"5": "#06B6D4", // Cyan
	"6": "#84CC16", // Lime
	"7": "#F97316", // Orange variant
	"8": "#EC4899", // Pink
	"9": "#8B5CF6", // Purple
	"10": "#059669", // Emerald
	"11": "#DC2626", // Rose
}

// Function to check if current colors match the default palette
const checkIfDefaultPalette = () => {
	// Check if all colors (both semantic and custom) match defaults
	const isDefault = colorNumbers.every((colorNumber) => {
		return colorValues.value[colorNumber] === defaultColors[colorNumber]
	})

	isDefaultPalette.value = isDefault

	return isDefault
}

// Function to restore default colors
const resetToDefaultColors = () => {
	colorNumbers.forEach((colorNumber) => {
		colorValues.value[colorNumber] = defaultColors[colorNumber]
		if (hexInputValues.value) {
			hexInputValues.value[colorNumber] = defaultColors[colorNumber]
		}
	})
}

// Function to ensure colorScheme structure exists
const ensureColorSchemeStructure = () => {
	if (!selectedTemplate.value.selectedTheme.semantic) {
		selectedTemplate.value.selectedTheme.semantic = new Semantic()
	}

	const semantic = selectedTemplate.value.selectedTheme.semantic
	if (!semantic.colorScheme) {
		semantic.colorScheme = new ColorSchemeMain({
			light: new SeverityMain({
				primary: "",
				secondary: "",
				accent: "",
				primaryGradations: {},
				secondaryGradations: {},
				accentGradations: {},
			}),
		})
	}
	if (!semantic.colorScheme.light) {
		semantic.colorScheme.light = new SeverityMain({
			primary: "",
			secondary: "",
			accent: "",
			primaryGradations: {},
			secondaryGradations: {},
			accentGradations: {},
		})
	}
}

// Dedicated function to update the Color value
const updateColorValue = (colorNumber: ColorNumber, newColor: any) => {
	// Extract hex value from Color
	const hexColor = typeof newColor === "string" ? newColor : newColor?.value || newColor

	// Ensure it starts with #
	const formattedColor = hexColor?.startsWith?.("#") ? hexColor : `#${hexColor}`

	// Update the Color in palette and hex input
	colorValues.value[colorNumber] = formattedColor
	if (hexInputValues.value) {
		hexInputValues.value[colorNumber] = formattedColor
	}

	// If this is a semantic color (0, 1, 2), update the corresponding semantic value in themeState
	if (isSemanticColor(colorNumber) && selectedTemplate.value.selectedTheme?.semantic) {
		switch (colorNumber) {
			case "0":
				ensureColorSchemeStructure()
				selectedTemplate.value.selectedTheme.semantic.colorScheme.light.primary = formattedColor
				// Also update semantic.primary if it exists and is an object with .color
				if (selectedTemplate.value.selectedTheme.semantic.primary && typeof selectedTemplate.value.selectedTheme.semantic.primary === "object") {
					// @ts-ignore - hybrid structure handling
					selectedTemplate.value.selectedTheme.semantic.primary.color = formattedColor
				}
				break
			case "1":
				ensureColorSchemeStructure()
				selectedTemplate.value.selectedTheme.semantic.colorScheme.light.secondary = formattedColor
				// Also update semantic.secondary if it exists and is an object with .color
				if (selectedTemplate.value.selectedTheme.semantic.secondary && typeof selectedTemplate.value.selectedTheme.semantic.secondary === "object") {
					// @ts-ignore - hybrid structure handling
					selectedTemplate.value.selectedTheme.semantic.secondary.color = formattedColor
				}
				break
			case "2":
				ensureColorSchemeStructure()
				selectedTemplate.value.selectedTheme.semantic.colorScheme.light.accent = formattedColor
				// Also update semantic.accent if it exists and is an object with .color
				if (selectedTemplate.value.selectedTheme.semantic.accent && typeof selectedTemplate.value.selectedTheme.semantic.accent === "object") {
					// @ts-ignore - hybrid structure handling
					selectedTemplate.value.selectedTheme.semantic.accent.color = formattedColor
				}
				break
		}
	}

	// Automatically check if palette matches defaults
	checkIfDefaultPalette()

	// Update selected Color for picker if necessary
	if (selectedColorForPickerNumber.value === colorNumber) {
		selectedColorForPicker.value = formattedColor
	}
}

// Function to handle hex input changes
const onHexInputChange = (colorNumber: ColorNumber) => {
	if (!hexInputValues.value) return

	const hexValue = hexInputValues.value[colorNumber]
	if (!hexValue) return

	// Validate hex color format
	const hexPattern = /^#?([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/
	let formattedHex = hexValue.trim()

	// Add # if not present
	if (!formattedHex.startsWith("#")) {
		formattedHex = "#" + formattedHex
	}

	if (hexPattern.test(formattedHex)) {
		updateColorValue(colorNumber, formattedHex)
	} else {
		// Revert to current color value if invalid
		hexInputValues.value[colorNumber] = colorValues.value[colorNumber]
	}
}

// Undo system functions
// Save current theme state to history
const saveThemeToHistory = () => {
	if (!selectedTemplate.value.selectedTheme) return

	// Only save if there was a previous state to compare against
	if (themeBeforePickerOpen) {
		// Check if theme actually changed by comparing JSON strings
		const currentTheme = JSON.stringify(selectedTemplate.value.selectedTheme)
		const previousTheme = JSON.stringify(themeBeforePickerOpen)

		if (currentTheme !== previousTheme || previousTheme === null) {
			// Save the state that existed BEFORE the changes
			themeHistory.value.push(JSON.parse(JSON.stringify(themeBeforePickerOpen)))
			hasColorChanges.value = themeHistory.value.length > 0

			// Keep only last 10 states
			if (themeHistory.value.length > 10) {
				themeHistory.value.shift()
			}
		}
		themeBeforePickerOpen = null
	}
}

// Restore the last theme state from history
const undoLastChange = async () => {
	if (themeHistory.value.length === 0) return

	// Get the last saved theme state
	const previousTheme = themeHistory.value.pop()
	if (!previousTheme) return

	// Restore the entire selectedTheme object
	if (selectedTemplate.value.selectedTheme) {
		selectedTemplate.value.selectedTheme = JSON.parse(JSON.stringify(previousTheme))

		// Force reactivity update
		selectedTemplate.value = { ...selectedTemplate.value }

		// Update hasColorChanges flag
		hasColorChanges.value = themeHistory.value.length > 0

		// Update palette to reflect restored theme (without reloading from DB)
		await loadColorsFromCurrentTheme()

		// Show success message
		toast.add({
			severity: "success",
			summary: "Undo Successful",
			detail: "Theme has been restored to previous state.",
			life: 3000,
		})
	}
}

// Reset theme history (called when switching themes)
const resetThemeHistory = async () => {
	themeHistory.value = []
	hasColorChanges.value = false
	themeBeforePickerOpen = selectedTemplate.value.selectedTheme ? JSON.parse(JSON.stringify(selectedTemplate.value.selectedTheme)) : null
}

// Function to handle color selection with advanced protection - now shows dialog
const handleColorSelected = (color: string) => {
	// Multiple protection against template loading
	if (isLoadingTemplate.value) {
		return
	}

	// Store the base color and show the scheme selection dialog
	baseColorForScheme.value = color
	selectedColorSchemeType.value = "" // Reset selection
	showColorSchemeDialog.value = true
}

// Function to generate a palette of 9 harmonic colors for array colors (3-11)
const generateHarmonicPalette = (baseColor: string): string[] => {
	const { h, s, l } = hexToHsl(baseColor)
	const palette: string[] = []

	// Color 0 (for array index 3): Lightened complementary
	palette[0] = hslToHex((h + 180) % 360, Math.max(s - 0.2, 0.3), Math.min(l + 0.2, 0.8))

	// Color 1 (for array index 4): Triadic +120°
	palette[1] = hslToHex((h + 120) % 360, Math.min(s + 0.1, 1.0), l)

	// Color 2 (for array index 5): Dark monochromatic
	palette[2] = hslToHex(h, s, Math.max(l - 0.3, 0.2))

	// Color 3 (for array index 6): Light monochromatic
	palette[3] = hslToHex(h, Math.max(s - 0.3, 0.2), Math.min(l + 0.3, 0.9))

	// Color 4 (for array index 7): Tetradic +90°
	palette[4] = hslToHex((h + 90) % 360, Math.min(s + 0.05, 0.9), Math.min(l + 0.1, 0.8))

	// Color 5 (for array index 8): Split-complementary +150°
	palette[5] = hslToHex((h + 150) % 360, Math.max(s - 0.1, 0.4), Math.min(l + 0.15, 0.85))

	// Color 6 (for array index 9): Split-complementary -150°
	palette[6] = hslToHex((h - 150 + 360) % 360, Math.min(s + 0.2, 1.0), Math.max(l - 0.1, 0.3))

	// Color 7 (for array index 10): Analogous +60°
	palette[7] = hslToHex((h + 60) % 360, Math.min(s, 0.9), Math.min(l + 0.05, 0.85))

	// Color 8 (for array index 11): Analogous -60°
	palette[8] = hslToHex((h - 60 + 360) % 360, Math.min(s, 0.9), Math.max(l - 0.05, 0.15))

	return palette
}

// Function to convert hex to HSL
const hexToHsl = (hex: string): { h: number; s: number; l: number } => {
	const cleanHex = hex.replace("#", "")

	const r = parseInt(cleanHex.substring(0, 2), 16) / 255
	const g = parseInt(cleanHex.substring(2, 4), 16) / 255
	const b = parseInt(cleanHex.substring(4, 6), 16) / 255

	const max = Math.max(r, g, b)
	const min = Math.min(r, g, b)
	const delta = max - min

	const l = (max + min) / 2

	let s = 0
	if (delta !== 0) {
		s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min)
	}

	let h = 0
	if (delta !== 0) {
		switch (max) {
			case r:
				h = ((g - b) / delta) % 6
				break
			case g:
				h = (b - r) / delta + 2
				break
			case b:
				h = (r - g) / delta + 4
				break
		}
		h *= 60
		if (h < 0) h += 360
	}

	return { h, s, l }
}

// Function to convert HSL to hex
const hslToHex = (h: number, s: number, l: number): string => {
	const c = (1 - Math.abs(2 * l - 1)) * s
	const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
	const m = l - c / 2

	let r = 0,
		g = 0,
		b = 0

	if (0 <= h && h < 60) {
		r = c
		g = x
		b = 0
	} else if (60 <= h && h < 120) {
		r = x
		g = c
		b = 0
	} else if (120 <= h && h < 180) {
		r = 0
		g = c
		b = x
	} else if (180 <= h && h < 240) {
		r = 0
		g = x
		b = c
	} else if (240 <= h && h < 300) {
		r = x
		g = 0
		b = c
	} else if (300 <= h && h < 360) {
		r = c
		g = 0
		b = x
	}

	const rHex = Math.round((r + m) * 255)
		.toString(16)
		.padStart(2, "0")
	const gHex = Math.round((g + m) * 255)
		.toString(16)
		.padStart(2, "0")
	const bHex = Math.round((b + m) * 255)
		.toString(16)
		.padStart(2, "0")

	return `#${rHex}${gHex}${bHex}`
}

const validateAndApplyHex = (colorNumber: ColorNumber) => {
	if (!hexInputValues.value) return

	if (isValidHexColor(hexInputValues.value[colorNumber])) {
		colorValues.value[colorNumber] = hexInputValues.value[colorNumber]
		checkIfDefaultPalette()
	} else {
		// Reset to current selected color if invalid
		hexInputValues.value[colorNumber] = colorValues.value[colorNumber]
	}
}

const isValidHexColor = (hex: string): boolean => {
	return /^#[0-9A-Fa-f]{6}$/.test(hex)
}

const sendColorToParent = (colorNumber: ColorNumber) => {
	if (isValidHexColor(colorValues.value[colorNumber])) {
		emit("colorSelected", colorValues.value[colorNumber])
	}
}

// Functions for create theme dialog
const openCreateThemeDialog = () => {
	// Populate base themes dropdown with all available options
	const baseThemes: Array<{ label: string; value: string }> = []
	// Add existing templates to the list
	templates.value.forEach((template) => {
		if (template.templateName && template.id) {
			baseThemes.push({
				label: template.templateName,
				value: template.id,
			})
		}
	})

	availableBaseThemes.value = baseThemes
	showCreateThemeDialog.value = true
	showValidationErrors.value = false
	newThemeName.value = ""

	// Find and select the actual default theme from the available templates
	const defaultTemplate = templates.value.find((t) => t.isDefaultTemplate)
	if (defaultTemplate && defaultTemplate.id) {
		selectedBaseTheme.value = defaultTemplate.id
	} else {
		selectedBaseTheme.value = "default" // Fallback to "default" if no default template found
	}
}

const closeCreateThemeDialog = () => {
	showCreateThemeDialog.value = false
	showValidationErrors.value = false
	newThemeName.value = ""
	selectedBaseTheme.value = ""
}

const createNewTheme = async () => {
	// Validate inputs - both theme name and base theme are required
	if (!newThemeName.value.trim() || !selectedBaseTheme.value) {
		showValidationErrors.value = true
		return
	}

	try {
		// Find the base theme to copy from
		let baseTemplate: ThemeState | null = null

		if (selectedBaseTheme.value === "default") {
			// Create a new default theme
			baseTemplate = createDefaultTheme()
		} else if (selectedBaseTheme.value === "standard") {
			// Create a new standard theme
			baseTemplate = createStandardTheme()
		} else {
			// Copy from existing template
			const existingTemplate = templates.value.find((t) => t.id === selectedBaseTheme.value)
			if (existingTemplate) {
				baseTemplate = (await themeStore.loadSingleCompleteTemplate(existingTemplate.id!, "configuration")) || null
			}
		}

		if (!baseTemplate) {
			toast.add({
				severity: "error",
				summary: "Error Loading Base Theme",
				detail: "Unable to load the selected base theme. Please try again.",
				life: 5000,
			})
			return
		}

		// Create new theme with new name - deep copy to avoid modifying original
		const newTemplateUI = new TemplateUI()

		// Copy all properties from base template UI
		if (baseTemplate.templateUI) {
			Object.assign(newTemplateUI, JSON.parse(JSON.stringify(baseTemplate.templateUI)))
		}

		// Set new theme properties
		newTemplateUI.id = undefined // Will be generated by API
		newTemplateUI.templateName = newThemeName.value.trim()
		// New themes should NEVER be default themes, regardless of base theme
		// This ensures the original default theme remains the only default
		newTemplateUI.isDefaultTemplate = false

		newTemplateUI.isStandardTheme = false // Ensure it's not a standard theme
		newTemplateUI.createdTimestamp = new Date()
		newTemplateUI.updatedTimestamp = new Date()

		// Create completely new theme object
		const newTheme = new ThemeState()
		newTheme.templateUI = newTemplateUI

		// Deep copy the selectedTheme if it exists - recreate with proper constructors
		if (baseTemplate.selectedTheme) {
			newTheme.selectedTheme = new ThemeTemplate()

			// Use init method if available, otherwise fallback to Object.assign
			if (typeof newTheme.selectedTheme.init === "function") {
				newTheme.selectedTheme.init(JSON.parse(JSON.stringify(baseTemplate.selectedTheme)))
			} else {
				Object.assign(newTheme.selectedTheme, JSON.parse(JSON.stringify(baseTemplate.selectedTheme)))
			}

			// Ensure semantic object is properly constructed if it exists
			if (baseTemplate.selectedTheme.semantic) {
				newTheme.selectedTheme.semantic = new Semantic()
				if (typeof newTheme.selectedTheme.semantic.init === "function") {
					newTheme.selectedTheme.semantic.init(JSON.parse(JSON.stringify(baseTemplate.selectedTheme.semantic)))
				} else {
					Object.assign(newTheme.selectedTheme.semantic, JSON.parse(JSON.stringify(baseTemplate.selectedTheme.semantic)))
				}
			}
		}

		// Ensure the main ThemeState object has proper methods
		if (typeof newTheme.init === "function") {
			// Re-initialize to ensure all methods are properly bound
			const themeData = JSON.parse(JSON.stringify(newTheme))
			newTheme.init(themeData)
		}

		// Save the new theme
		const savedTheme = await themeStore.saveSingleTemplate(newTheme)

		console.log("🎨 New theme created:", savedTheme)

		// Close dialog first - this completes all UI operations
		closeCreateThemeDialog()

		// Auto-select and load the newly created theme AFTER everything else is done
		if (savedTheme && savedTheme.templateUI?.id) {
			const newThemeId = savedTheme.templateUI.id.toString()

			// Check for valid GUID (not all zeros)
			if (newThemeId && newThemeId !== "00000000-0000-0000-0000-000000000000") {
				// Wait for all DOM updates from closing dialog
				await nextTick()

				// First, refresh templates list without selecting anything
				await loadTemplatesFromDB(false)

				// Wait for templates list to be fully rendered
				await nextTick()

				// Find the new theme in the updated list
				const newThemeInList = templates.value.find((t) => t.id === newThemeId || t.id?.toString() === newThemeId)
				if (newThemeInList) {
					// Now perform the selection - wait for each step

					selectedTemplateId.value = newThemeId

					// Wait for the reactive change to propagate
					await nextTick()

					// Load the theme configuration

					await loadTemplateFromDB()
				} else {
					console.error("🎨 ❌ New theme with ID", newThemeId, "not found in templates list!")
					console.error(
						"🎨 Available themes:",
						templates.value.map((t) => ({ id: t.id, name: t.templateName }))
					)

					// Retry mechanism with longer delay

					await new Promise((resolve) => setTimeout(resolve, 1000))
					await loadTemplatesFromDB(false)
					await nextTick()

					const retryTheme = templates.value.find((t) => t.id === newThemeId || t.id?.toString() === newThemeId)
					if (retryTheme) {
						selectedTemplateId.value = newThemeId
						await nextTick()
						await loadTemplateFromDB()
					} else {
						console.error("🎨 ❌ New theme still not found after retry")
						console.error(
							"🎨 Final available themes:",
							templates.value.map((t) => ({ id: t.id, name: t.templateName }))
						)
					}
				}
			} else {
				console.error("🎨 ❌ Invalid or empty GUID received:", newThemeId)
				// Still refresh the templates list
				await loadTemplatesFromDB()
			}
		} else {
			console.error("🎨 ❌ savedTheme or ID is missing:", savedTheme)
			// Still refresh the templates list
			await loadTemplatesFromDB()
		}

		// Show success toast regardless of auto-load success
		toast.add({
			severity: "success",
			summary: "Theme Created Successfully!",
			detail: `"${newThemeName.value}" has been created. ${savedTheme?.templateUI?.id ? "It has been automatically selected." : "Please select it from the dropdown to begin customization."}`,
			life: 5000,
		})
	} catch (error) {
		console.error("Error creating new theme:", error)
		toast.add({
			severity: "error",
			summary: "Error Creating Theme",
			detail: `Failed to create theme: ${(error as any).message}`,
			life: 5000,
		})
	}
}

// Helper functions to create default themes
const createDefaultTheme = (): ThemeState => {
	const defaultTheme = new ThemeState()
	defaultTheme.templateUI = new TemplateUI()
	defaultTheme.selectedTheme = new ThemeTemplate()
	defaultTheme.selectedTheme.semantic = new Semantic()

	// Set default colors in colorScheme.light structure
	defaultTheme.selectedTheme.semantic.colorScheme = {
		light: {
			primary: "#3B82F6",
			secondary: "#EF4444",
			accent: "#6366F1",
		},
	}

	return defaultTheme
}

const createStandardTheme = (): ThemeState => {
	// Similar to default but with different color scheme
	const standardTheme = createDefaultTheme()
	if (standardTheme.selectedTheme?.semantic) {
		standardTheme.selectedTheme.semantic.colorScheme = {
			light: {
				primary: "#1D4ED8",
				secondary: "#DC2626",
				accent: "#7C3AED",
			},
		}
	}

	return standardTheme
}

// Functions for color scheme generator dialog
const openColorSchemeGeneratorDialog = () => {
	showColorSchemeGeneratorDialog.value = true
	selectedGeneratorSchemeType.value = "" // Reset selection
	showGeneratorColorPicker.value = false // Reset color picker
}

const closeColorSchemeGeneratorDialog = () => {
	showColorSchemeGeneratorDialog.value = false
	selectedGeneratorSchemeType.value = ""
	showGeneratorColorPicker.value = false
}

// Function to get detailed explanation for each color scheme type
const getColorSchemeDetailedExplanation = (schemeType: string): string => {
	const explanations: Record<string, string> = {
		monochromatic:
			"This scheme creates a harmonious and cohesive look by using different shades, tints, and tones of the same base color. The primary color will be your selected color, while secondary and accent colors will be lighter and darker variations. Perfect for creating a unified, professional appearance with subtle visual hierarchy.",

		analogous:
			"This scheme uses colors that sit next to each other on the color wheel (typically within 30-60 degrees). Starting from your base color, it selects neighboring hues to create a serene and comfortable design. The result is naturally pleasing to the eye with smooth color transitions, ideal for nature-inspired or calming interfaces.",

		complementary:
			"This high-contrast scheme pairs your base color with its direct opposite on the color wheel (180 degrees apart). This creates maximum visual impact and vibrancy. The primary will be your base color, with the complement used for accents to create visual interest and draw attention to important elements.",

		triadic:
			"This vibrant scheme uses three colors equally spaced around the color wheel (120 degrees apart). It offers strong visual contrast while maintaining color harmony. The result is a bold, playful palette that works well for creative applications while still feeling balanced.",

		splitComplementary:
			"A variation of the complementary scheme that uses your base color plus the two colors adjacent to its complement. This provides high contrast with more nuance than a straight complementary scheme, offering visual interest without being as jarring. Great for designs that need to be eye-catching but not overwhelming.",

		tetradic:
			"Also known as double-complementary, this scheme uses four colors arranged into two complementary pairs. It offers the richest variety of colors while maintaining balance. Best suited for experienced designers as it requires careful balance to avoid overwhelming the interface. One color should dominate while others support.",

		business:
			"A conservative, professional palette optimized for corporate and business applications. Uses muted, sophisticated colors with controlled saturation levels. Primary colors are kept professional (blues, grays), with subtle accent colors for highlights. Ensures excellent readability and a trustworthy appearance.",

		creative:
			"A bold, vibrant palette designed for creative and artistic applications. Features higher saturation and more adventurous color combinations. Uses dynamic contrasts and unexpected color pairings to create energy and excitement while maintaining usability. Perfect for brands that want to stand out and express creativity.",
	}

	return explanations[schemeType] || "Select a color scheme type to see how it will transform your theme colors."
}

const toggleGeneratorColorPicker = () => {
	showGeneratorColorPicker.value = !showGeneratorColorPicker.value
}

const updateGeneratorColor = (newColor: any) => {
	const hexColor = typeof newColor === "string" ? newColor : newColor?.value || newColor
	const formattedColor = hexColor?.startsWith?.("#") ? hexColor : `#${hexColor}`
	generatorBaseColor.value = formattedColor
}

const applyGeneratorColorScheme = () => {
	if (!selectedGeneratorSchemeType.value) return

	// Save current state to undo history before applying scheme
	saveThemeToHistory()

	// Generate semantic colors based on selected scheme
	const semanticColors = generateSemanticColors(generatorBaseColor.value, selectedGeneratorSchemeType.value)

	// Update semantic colors (0-2)
	colorValues.value["0"] = semanticColors.primary
	colorValues.value["1"] = semanticColors.secondary
	colorValues.value["2"] = semanticColors.accent

	// Update hex input values for semantic colors
	hexInputValues.value["0"] = semanticColors.primary
	hexInputValues.value["1"] = semanticColors.secondary
	hexInputValues.value["2"] = semanticColors.accent

	// Generate array colors (3-11) using existing function
	const harmonicPalette = generateHarmonicPalette(generatorBaseColor.value)
	const arrayColorNumbers = ["3", "4", "5", "6", "7", "8", "9", "10", "11"] as const
	arrayColorNumbers.forEach((colorNumber, index) => {
		colorValues.value[colorNumber] = harmonicPalette[index]
		if (hexInputValues.value) {
			hexInputValues.value[colorNumber] = harmonicPalette[index]
		}
	})

	// Update semantic colors in theme state if available
	if (selectedTemplate.value.selectedTheme?.semantic) {
		ensureColorSchemeStructure()
		selectedTemplate.value.selectedTheme.semantic.colorScheme.light.primary = semanticColors.primary
		selectedTemplate.value.selectedTheme.semantic.colorScheme.light.secondary = semanticColors.secondary
		selectedTemplate.value.selectedTheme.semantic.colorScheme.light.accent = semanticColors.accent

		// Also update semantic.primary/secondary/accent if they exist as objects with .color
		if (selectedTemplate.value.selectedTheme.semantic.primary && typeof selectedTemplate.value.selectedTheme.semantic.primary === "object") {
			// @ts-ignore - hybrid structure handling
			selectedTemplate.value.selectedTheme.semantic.primary.color = semanticColors.primary
		}
		if (selectedTemplate.value.selectedTheme.semantic.secondary && typeof selectedTemplate.value.selectedTheme.semantic.secondary === "object") {
			// @ts-ignore - hybrid structure handling
			selectedTemplate.value.selectedTheme.semantic.secondary.color = semanticColors.secondary
		}
		if (selectedTemplate.value.selectedTheme.semantic.accent && typeof selectedTemplate.value.selectedTheme.semantic.accent === "object") {
			// @ts-ignore - hybrid structure handling
			selectedTemplate.value.selectedTheme.semantic.accent.color = semanticColors.accent
		}
	}

	// Check if new palette matches defaults
	checkIfDefaultPalette()

	// Close dialog
	closeColorSchemeGeneratorDialog()
}

// Functions for inline theme name editing
const themeNameInputRef = ref()

const startEditingThemeName = () => {
	if (!selectedTemplateId.value) return

	// Get current theme name
	const currentTheme = templates.value.find((t) => t.id === selectedTemplateId.value)
	editingThemeName.value = currentTheme?.templateName || ""
	isEditingThemeName.value = true

	// Focus the input field after it's rendered
	nextTick(() => {
		if (themeNameInputRef.value) {
			themeNameInputRef.value.$el.focus()
		}
	})
}

const cancelEditingThemeName = () => {
	isEditingThemeName.value = false
	editingThemeName.value = ""
}

const saveInlineThemeName = async () => {
	if (!editingThemeName.value.trim() || !selectedTemplateId.value) return

	try {
		// Store the current selection before updating
		const currentSelectedId = selectedTemplateId.value

		// Update the theme name in the current template
		if (selectedTemplate.value.templateUI) {
			selectedTemplate.value.templateUI.templateName = editingThemeName.value.trim()
		}

		// Use the new API endpoint to update only the theme name
		await themeMgmClient.updateThemeName(selectedTemplateId.value, editingThemeName.value.trim())

		// Refresh the templates list to show the updated name without changing selection
		await loadTemplatesFromDB(true, currentSelectedId)

		// Exit edit mode
		isEditingThemeName.value = false
		editingThemeName.value = ""
	} catch (error) {
		console.error("Error saving theme name:", error)
		alert("Error saving theme name: " + (error as any).message)
		// Stay in edit mode on error
	}
}

// Function to get tooltip text for edit name button
const getEditNameTooltip = () => {
	if (!selectedTemplateId.value) {
		return "Select a theme to edit its name"
	}
	if (isStandardTheme.value && !overrideStandardTheme.value) {
		return "Cannot edit name of standard theme. Enable override to allow editing."
	}
	return "Click to edit theme name"
}

// Functions for color scheme dialog
const closeColorSchemeDialog = () => {
	showColorSchemeDialog.value = false
	selectedColorSchemeType.value = ""
	baseColorForScheme.value = ""
}

const applySelectedColorScheme = () => {
	if (!selectedColorSchemeType.value || !baseColorForScheme.value) return

	// Save current state to undo history before applying scheme
	saveThemeToHistory()

	// Generate semantic colors based on selected scheme
	const semanticColors = generateSemanticColors(baseColorForScheme.value, selectedColorSchemeType.value)

	// Update semantic colors (0-2)
	colorValues.value["0"] = semanticColors.primary
	colorValues.value["1"] = semanticColors.secondary
	colorValues.value["2"] = semanticColors.accent

	// Update hex input values for semantic colors
	hexInputValues.value["0"] = semanticColors.primary
	hexInputValues.value["1"] = semanticColors.secondary
	hexInputValues.value["2"] = semanticColors.accent

	// Generate array colors (3-11) using existing function
	const harmonicPalette = generateHarmonicPalette(baseColorForScheme.value)
	const arrayColorNumbers = ["3", "4", "5", "6", "7", "8", "9", "10", "11"] as const
	arrayColorNumbers.forEach((colorNumber, index) => {
		colorValues.value[colorNumber] = harmonicPalette[index]
		if (hexInputValues.value) {
			hexInputValues.value[colorNumber] = harmonicPalette[index]
		}
	})

	// Update semantic colors in theme state if available
	if (selectedTemplate.value.selectedTheme?.semantic) {
		ensureColorSchemeStructure()
		selectedTemplate.value.selectedTheme.semantic.colorScheme.light.primary = semanticColors.primary
		selectedTemplate.value.selectedTheme.semantic.colorScheme.light.secondary = semanticColors.secondary
		selectedTemplate.value.selectedTheme.semantic.colorScheme.light.accent = semanticColors.accent

		// Also update semantic.primary/secondary/accent if they exist as objects with .color
		if (selectedTemplate.value.selectedTheme.semantic.primary && typeof selectedTemplate.value.selectedTheme.semantic.primary === "object") {
			// @ts-ignore - hybrid structure handling
			selectedTemplate.value.selectedTheme.semantic.primary.color = semanticColors.primary
		}
		if (selectedTemplate.value.selectedTheme.semantic.secondary && typeof selectedTemplate.value.selectedTheme.semantic.secondary === "object") {
			// @ts-ignore - hybrid structure handling
			selectedTemplate.value.selectedTheme.semantic.secondary.color = semanticColors.secondary
		}
		if (selectedTemplate.value.selectedTheme.semantic.accent && typeof selectedTemplate.value.selectedTheme.semantic.accent === "object") {
			// @ts-ignore - hybrid structure handling
			selectedTemplate.value.selectedTheme.semantic.accent.color = semanticColors.accent
		}
	}

	// Check if new palette matches defaults
	checkIfDefaultPalette()

	// Close dialog
	closeColorSchemeDialog()
}

// Functions for delete theme functionality
const getDeleteTooltip = () => {
	if (!selectedTemplateId.value) {
		return "Select a theme to delete"
	}
	if (isStandardTheme.value && !overrideStandardTheme.value) {
		return "Cannot delete standard themes unless override protection is enabled"
	}
	return "Delete the selected theme"
}

const confirmDeleteTheme = () => {
	if (!selectedTemplateId.value) {
		toast.add({
			severity: "warn",
			summary: "No Theme Selected",
			detail: "Please select a theme to delete.",
			life: 3000,
		})
		return
	}

	// Find the selected theme
	const themeToDelete = templates.value.find((t) => t.id === selectedTemplateId.value)
	if (!themeToDelete) {
		toast.add({
			severity: "error",
			summary: "Theme Not Found",
			detail: "The selected theme could not be found.",
			life: 3000,
		})
		return
	}

	// Check if it's a default theme
	if (themeToDelete.isDefaultTemplate) {
		toast.add({
			severity: "warn",
			summary: "Cannot Delete Default Theme",
			detail: "Please make another theme the default before deleting this one.",
			life: 5000,
		})
		return
	}

	// Set the theme to delete and show confirmation dialog
	selectedThemeToDelete.value = themeToDelete
	showDeleteConfirmDialog.value = true
}

const closeDeleteConfirmDialog = () => {
	showDeleteConfirmDialog.value = false
	selectedThemeToDelete.value = null
	isDeletingTheme.value = false
}

// Functions for apply semantic colors dialog
const openApplySemanticColorsDialog = () => {
	showApplySemanticColorsDialog.value = true
	selectedApplyStrategy.value = "" // Reset selection
}

const closeApplySemanticColorsDialog = () => {
	showApplySemanticColorsDialog.value = false
	selectedApplyStrategy.value = ""
}

const getApplyStrategyExplanation = (strategy: string): string => {
	const explanations: Record<string, string> = {
		smart:
			"This strategy uses advanced color theory to automatically generate a complete theme based on your semantic colors. It will create harmonious color combinations for all UI elements, ensuring proper contrast ratios and visual hierarchy. The primary color drives the main interface elements, secondary provides supporting accents, and the accent color adds emphasis to key interactions.",

		components:
			"Maps your semantic colors directly to specific UI components. Primary color will be applied to buttons, navigation bars, and headers. Secondary color will style cards, sidebars, and secondary buttons. Accent color will highlight active states, links, and important notifications. This provides predictable and consistent component styling.",

		gradient:
			"Creates a sophisticated gradient system using your semantic colors as anchors. Generates 10 shades for each color (from lightest to darkest), providing subtle variations for hover states, backgrounds, and borders. This approach creates depth and dimension in your interface while maintaining color consistency.",

		monochrome:
			"Uses only your primary color to create a clean, minimalist theme. All interface elements will use different shades and tints of the primary color, with secondary and accent colors reserved for special states like warnings and errors. Perfect for professional, focused applications.",

		contrast:
			"Optimizes your theme for maximum accessibility and readability. Uses your primary color for main elements, ensures WCAG AAA contrast ratios between text and backgrounds, and applies secondary and accent colors strategically for visual hierarchy. Ideal for applications requiring high visibility and accessibility compliance.",
	}

	return explanations[strategy] || "Select a strategy to see how it will apply your semantic colors throughout the theme."
}

// Function to apply CSS variables for preview update
const applyCSSVariables = (variables: Record<string, string>) => {
	Object.entries(variables).forEach(([key, value]) => {
		document.documentElement.style.setProperty(key, value)
	})
}

// Function to apply colors to all preview screens
const applyColorsToPreview = (colors: {
	primary: string
	secondary: string
	accent: string
	menuBackground?: string
	menuText?: string
	menuBorder?: string
	sidebarBackground?: string
	sidebarText?: string
	cardHeaderBackground?: string
	cardHeaderText?: string
	buttonBackground?: string
	buttonText?: string
	loginBackground?: string
	loginButtonBackground?: string
	loginButtonText?: string
}) => {
	// Apply to Login Page CSS variables
	if (colors.loginBackground) {
		applyCSSVariables({
			"--lca-login-background-color": colors.loginBackground,
		})
	}
	if (colors.loginButtonBackground) {
		applyCSSVariables({
			"--lca-login-button-color": colors.loginButtonBackground,
			"--lca-login-text-color": colors.loginButtonText || "#ffffff",
			"--lca-login-button-hovercolor": colors.loginButtonText || "#ffffff",
		})
	}

	// Apply to Main Layout CSS variables
	if (colors.menuBackground) {
		applyCSSVariables({
			"--main-header-background-color": colors.menuBackground,
			"--main-header-text-color": colors.menuText || "#ffffff",
			"--main-header-borderbottom-color": colors.menuBorder || colors.accent,
		})
	}
	if (colors.sidebarBackground) {
		applyCSSVariables({
			"--main-sidebar-background-color": colors.sidebarBackground,
			"--main-sidebar-text-color": colors.sidebarText || "#ffffff",
		})
	}

	// Apply to Components CSS variables
	if (colors.buttonBackground) {
		// Apply to all button variants
		const buttons = document.querySelectorAll(".p-button-primary, .p-button")
		buttons.forEach((btn) => {
			const htmlBtn = btn as HTMLElement
			htmlBtn.style.setProperty("background-color", colors.buttonBackground!, "important")
			htmlBtn.style.setProperty("border-color", colors.buttonBackground!, "important")
			htmlBtn.style.setProperty("color", colors.buttonText || "#ffffff", "important")
		})
	}

	// Apply to checkboxes
	if (colors.primary) {
		const checkboxes = document.querySelectorAll(".p-checkbox-checked .p-checkbox-box")
		checkboxes.forEach((box) => {
			const htmlBox = box as HTMLElement
			htmlBox.style.setProperty("background-color", colors.primary, "important")
			htmlBox.style.setProperty("border-color", colors.primary, "important")
		})
	}

	// Apply to tabs
	if (colors.primary) {
		const activeTabs = document.querySelectorAll(".p-tabview-nav-link.p-tabview-selected")
		activeTabs.forEach((tab) => {
			const htmlTab = tab as HTMLElement
			htmlTab.style.setProperty("color", colors.primary, "important")
			htmlTab.style.setProperty("border-bottom", `3px solid ${colors.primary}`, "important")
		})
	}
}

const applySemanticColorsToTheme = () => {
	if (!selectedApplyStrategy.value || !selectedTemplate.value) return

	const primary = colorValues.value["0"]
	const secondary = colorValues.value["1"]
	const accent = colorValues.value["2"]

	// Apply colors based on selected strategy
	switch (selectedApplyStrategy.value) {
		case "smart":
			applySmartThemeGeneration(primary, secondary, accent)
			break
		case "components":
			applyComponentBasedMapping(primary, secondary, accent)
			break
		case "gradient":
			applyGradientVariations(primary, secondary, accent)
			break
		case "monochrome":
			applyMonochromaticScheme(primary, secondary, accent)
			break
		case "contrast":
			applyHighContrastMode(primary, secondary, accent)
			break
	}

	// Check if palette matches defaults after changes
	checkIfDefaultPalette()

	// Close dialog
	closeApplySemanticColorsDialog()

	const strategyLabel = applyStrategies.find((s) => s.value === selectedApplyStrategy.value)?.label || "selected"
	toast.add({
		severity: "success",
		summary: "Theme Updated",
		detail: `Applied semantic colors using ${strategyLabel} strategy`,
		life: 3000,
	})
}

// Helper functions for each strategy
const applySmartThemeGeneration = (primary: string, secondary: string, accent: string) => {
	// Generate color variations
	const primaryVariations = generateColorScale(primary)
	const secondaryVariations = generateColorScale(secondary)
	const accentVariations = generateColorScale(accent)

	// Ensure the theme structure exists
	if (!selectedTemplate.value.selectedTheme) {
		selectedTemplate.value.selectedTheme = new ThemeTemplate()
	}
	if (!selectedTemplate.value.selectedTheme.semantic) {
		selectedTemplate.value.selectedTheme.semantic = new Semantic()
	}

	// Update semantic colors in the template
	const semantic = selectedTemplate.value.selectedTheme.semantic

	// Initialize objects if they don't exist
	if (!semantic.loginScreen) semantic.loginScreen = {}
	if (!semantic.menuUp) semantic.menuUp = {}
	if (!semantic.sidebar) semantic.sidebar = {}
	if (!semantic.cardHeader) semantic.cardHeader = {}

	// Login screen
	semantic.loginScreen.loginButton = {
		backgroundColor: primary,
		backgroundColorHover: primaryVariations[6],
		textColor: "#ffffff",
	}
	semantic.loginScreen.loginBackground = {
		color: primaryVariations[0],
	}

	// Menu and navigation
	semantic.menuUp.backgroundColor = primary
	semantic.menuUp.borderBottom = accent
	semantic.menuUp.textColor = "#ffffff"
	semantic.menuUp.buttonColor = primary
	semantic.menuUp.buttonColorHover = primaryVariations[6]

	// Sidebar
	semantic.sidebar.backgroundColor = primaryVariations[0]
	semantic.sidebar.textColor = "#ffffff"

	// Card headers
	semantic.cardHeader.backgroundColor = secondaryVariations[5]
	semantic.cardHeader.textColor = "#ffffff"

	// Update components
	if (!selectedTemplate.value.selectedTheme.components) {
		selectedTemplate.value.selectedTheme.components = {}
	}
	const components = selectedTemplate.value.selectedTheme.components

	// Initialize component structures if they don't exist
	if (!components.button) components.button = { colorScheme: { light: { root: { primary: {} } } } }
	if (!components.checkbox) components.checkbox = { colorScheme: { light: { root: {} } } }
	if (!components.tabs) components.tabs = { colorScheme: { light: { root: { tab: {} } } } }

	// Buttons
	if (!components.button.colorScheme) components.button.colorScheme = { light: { root: { primary: {} } } }
	if (!components.button.colorScheme.light) components.button.colorScheme.light = { root: { primary: {} } }
	if (!components.button.colorScheme.light.root) components.button.colorScheme.light.root = { primary: {} }
	if (!components.button.colorScheme.light.root.primary) components.button.colorScheme.light.root.primary = {}

	components.button.colorScheme.light.root.primary.background = primary
	components.button.colorScheme.light.root.primary.hoverBackground = primaryVariations[6]
	components.button.colorScheme.light.root.primary.borderColor = primary
	components.button.colorScheme.light.root.primary.color = "#ffffff"

	// Checkboxes
	if (!components.checkbox.colorScheme.light.root) components.checkbox.colorScheme.light.root = {}
	components.checkbox.colorScheme.light.root.checkedBackground = primary
	components.checkbox.colorScheme.light.root.hoverBackground = primaryVariations[6]

	// Tabs
	if (!components.tabs.colorScheme.light.root) components.tabs.colorScheme.light.root = { tab: {} }
	components.tabs.colorScheme.light.root.activeBar = { background: primary }
	if (!components.tabs.colorScheme.light.root.tab) components.tabs.colorScheme.light.root.tab = {}
	components.tabs.colorScheme.light.root.tab.activeBorderColor = primary
	components.tabs.colorScheme.light.root.tab.activeColor = primary
	components.tabs.colorScheme.light.root.tab.activeBar = primary

	// Apply colors to preview using CSS variables
	applyColorsToPreview({
		primary,
		secondary,
		accent,
		loginBackground: primaryVariations[0],
		loginButtonBackground: primary,
		loginButtonText: "#ffffff",
		menuBackground: primary,
		menuText: "#ffffff",
		menuBorder: accent,
		sidebarBackground: primaryVariations[0],
		sidebarText: "#ffffff",
		cardHeaderBackground: secondaryVariations[5],
		cardHeaderText: "#ffffff",
		buttonBackground: primary,
		buttonText: "#ffffff",
	})

	// Emit update to trigger preview refresh
	emit("updateTheme", selectedTemplate.value)
}

const applyComponentBasedMapping = (primary: string, secondary: string, accent: string) => {
	// Ensure structure exists
	if (!selectedTemplate.value.selectedTheme) {
		selectedTemplate.value.selectedTheme = new ThemeTemplate()
	}
	if (!selectedTemplate.value.selectedTheme.semantic) {
		selectedTemplate.value.selectedTheme.semantic = new Semantic()
	}
	if (!selectedTemplate.value.selectedTheme.components) {
		selectedTemplate.value.selectedTheme.components = {}
	}

	const semantic = selectedTemplate.value.selectedTheme.semantic
	const components = selectedTemplate.value.selectedTheme.components

	// Map primary to main interactive elements
	if (!components.button) components.button = { colorScheme: { light: { root: { primary: {} } } } }
	if (!components.button.colorScheme?.light?.root?.primary) {
		if (!components.button.colorScheme) components.button.colorScheme = { light: { root: { primary: {} } } }
		if (!components.button.colorScheme.light) components.button.colorScheme.light = { root: { primary: {} } }
		if (!components.button.colorScheme.light.root) components.button.colorScheme.light.root = { primary: {} }
		if (!components.button.colorScheme.light.root.primary) components.button.colorScheme.light.root.primary = {}
	}
	components.button.colorScheme.light.root.primary.background = primary
	components.button.colorScheme.light.root.primary.borderColor = primary
	components.button.colorScheme.light.root.primary.color = "#ffffff"

	// Map secondary to supporting elements
	if (!semantic.cardHeader) semantic.cardHeader = {}
	semantic.cardHeader.backgroundColor = secondary
	semantic.cardHeader.textColor = "#ffffff"

	// Map accent to highlights and notifications
	if (!components.toast) components.toast = { colorScheme: { light: { root: { info: {} } } } }
	if (!components.toast.colorScheme?.light?.root?.info) {
		if (!components.toast.colorScheme) components.toast.colorScheme = { light: { root: { info: {} } } }
		if (!components.toast.colorScheme.light) components.toast.colorScheme.light = { root: { info: {} } }
		if (!components.toast.colorScheme.light.root) components.toast.colorScheme.light.root = { info: {} }
		if (!components.toast.colorScheme.light.root.info) components.toast.colorScheme.light.root.info = {}
	}
	components.toast.colorScheme.light.root.info.background = accent
	components.toast.colorScheme.light.root.info.color = "#ffffff"

	// Apply colors to preview using CSS variables
	applyColorsToPreview({
		primary,
		secondary,
		accent,
		buttonBackground: primary,
		buttonText: "#ffffff",
		cardHeaderBackground: secondary,
		cardHeaderText: "#ffffff",
	})

	// Emit update to trigger preview refresh
	emit("updateTheme", selectedTemplate.value)
}

const applyGradientVariations = (primary: string, secondary: string, accent: string) => {
	// Generate gradient scales
	const primaryScale = generateColorScale(primary)
	const secondaryScale = generateColorScale(secondary)
	const accentScale = generateColorScale(accent)

	// Ensure structure exists
	if (!selectedTemplate.value.selectedTheme) {
		selectedTemplate.value.selectedTheme = new ThemeTemplate()
	}
	if (!selectedTemplate.value.selectedTheme.semantic) {
		selectedTemplate.value.selectedTheme.semantic = new Semantic()
	}

	const semantic = selectedTemplate.value.selectedTheme.semantic

	// Initialize ermbrand if it doesn't exist
	if (!semantic.ermbrand) semantic.ermbrand = {}

	// Apply primary scale to first color group
	if (!semantic.ermbrand.green) semantic.ermbrand.green = {}
	for (let i = 0; i < 10; i++) {
		semantic.ermbrand.green[i.toString()] = primaryScale[i]
	}

	// Apply secondary scale to second color group
	if (!semantic.ermbrand.blue) semantic.ermbrand.blue = {}
	for (let i = 0; i < 10; i++) {
		semantic.ermbrand.blue[i.toString()] = secondaryScale[i]
	}

	// Apply accent scale to third color group
	if (!semantic.ermbrand.navyblue) semantic.ermbrand.navyblue = {}
	for (let i = 0; i < 10; i++) {
		semantic.ermbrand.navyblue[i.toString()] = accentScale[i]
	}

	// Apply gradient colors to preview
	applyColorsToPreview({
		primary: primaryScale[5],
		secondary: secondaryScale[5],
		accent: accentScale[5],
		loginBackground: primaryScale[1],
		loginButtonBackground: primaryScale[6],
		loginButtonText: "#ffffff",
		menuBackground: primaryScale[4],
		menuText: "#ffffff",
		menuBorder: accentScale[6],
		sidebarBackground: primaryScale[2],
		sidebarText: "#ffffff",
		cardHeaderBackground: secondaryScale[3],
		cardHeaderText: "#ffffff",
		buttonBackground: primaryScale[5],
		buttonText: "#ffffff",
	})

	// Emit update to trigger preview refresh
	emit("updateTheme", selectedTemplate.value)
}

const applyMonochromaticScheme = (primary: string, secondary: string, accent: string) => {
	// Generate monochromatic variations from primary color
	const monoScale = generateColorScale(primary)

	// Ensure structure exists
	if (!selectedTemplate.value.selectedTheme) {
		selectedTemplate.value.selectedTheme = new ThemeTemplate()
	}
	if (!selectedTemplate.value.selectedTheme.semantic) {
		selectedTemplate.value.selectedTheme.semantic = new Semantic()
	}

	const semantic = selectedTemplate.value.selectedTheme.semantic

	// Initialize objects if they don't exist
	if (!semantic.menuUp) semantic.menuUp = {}
	if (!semantic.sidebar) semantic.sidebar = {}
	if (!semantic.cardHeader) semantic.cardHeader = {}

	// Use different shades of primary for all elements
	semantic.menuUp.backgroundColor = monoScale[5]
	semantic.menuUp.textColor = "#ffffff"

	semantic.sidebar.backgroundColor = monoScale[1]
	semantic.sidebar.textColor = "#ffffff"

	semantic.cardHeader.backgroundColor = monoScale[7]
	semantic.cardHeader.textColor = monoScale[1]

	// Keep secondary and accent for special states only
	semantic.redSignal = accent
	semantic.yellowSignal = secondary

	// Apply monochromatic colors to preview
	applyColorsToPreview({
		primary: monoScale[5],
		secondary: monoScale[3],
		accent: monoScale[7],
		loginBackground: monoScale[0],
		loginButtonBackground: monoScale[5],
		loginButtonText: "#ffffff",
		menuBackground: monoScale[5],
		menuText: "#ffffff",
		menuBorder: monoScale[7],
		sidebarBackground: monoScale[1],
		sidebarText: "#ffffff",
		cardHeaderBackground: monoScale[7],
		cardHeaderText: monoScale[1],
		buttonBackground: monoScale[5],
		buttonText: "#ffffff",
	})

	// Emit update to trigger preview refresh
	emit("updateTheme", selectedTemplate.value)
}

const applyHighContrastMode = (primary: string, secondary: string, accent: string) => {
	// Ensure high contrast combinations
	const darkPrimary = ensureDarkColor(primary)
	const lightBackground = "#ffffff"
	const darkBackground = "#000000"

	// Ensure structure exists
	if (!selectedTemplate.value.selectedTheme) {
		selectedTemplate.value.selectedTheme = new ThemeTemplate()
	}
	if (!selectedTemplate.value.selectedTheme.semantic) {
		selectedTemplate.value.selectedTheme.semantic = new Semantic()
	}
	if (!selectedTemplate.value.selectedTheme.components) {
		selectedTemplate.value.selectedTheme.components = {}
	}

	const semantic = selectedTemplate.value.selectedTheme.semantic
	const components = selectedTemplate.value.selectedTheme.components

	// Initialize objects if they don't exist
	if (!semantic.menuUp) semantic.menuUp = {}
	if (!semantic.sidebar) semantic.sidebar = {}
	if (!semantic.textNormal) semantic.textNormal = {}

	// High contrast navigation
	semantic.menuUp.backgroundColor = darkPrimary
	semantic.menuUp.textColor = lightBackground
	semantic.menuUp.borderBottom = accent

	// High contrast sidebar
	semantic.sidebar.backgroundColor = darkBackground
	semantic.sidebar.textColor = lightBackground

	// Ensure text readability
	semantic.textNormal.color = darkBackground

	// Apply to components for consistency
	if (!components.button) components.button = { colorScheme: { light: { root: { primary: {} } } } }
	if (!components.button.colorScheme?.light?.root?.primary) {
		if (!components.button.colorScheme) components.button.colorScheme = { light: { root: { primary: {} } } }
		if (!components.button.colorScheme.light) components.button.colorScheme.light = { root: { primary: {} } }
		if (!components.button.colorScheme.light.root) components.button.colorScheme.light.root = { primary: {} }
		if (!components.button.colorScheme.light.root.primary) components.button.colorScheme.light.root.primary = {}
	}
	components.button.colorScheme.light.root.primary.background = darkPrimary
	components.button.colorScheme.light.root.primary.color = lightBackground

	// Apply high contrast colors to preview
	applyColorsToPreview({
		primary: darkPrimary,
		secondary,
		accent,
		loginBackground: darkBackground,
		loginButtonBackground: darkPrimary,
		loginButtonText: lightBackground,
		menuBackground: darkPrimary,
		menuText: lightBackground,
		menuBorder: accent,
		sidebarBackground: darkBackground,
		sidebarText: lightBackground,
		cardHeaderBackground: darkPrimary,
		cardHeaderText: lightBackground,
		buttonBackground: darkPrimary,
		buttonText: lightBackground,
	})

	// Emit update to trigger preview refresh
	emit("updateTheme", selectedTemplate.value)
}

// Helper function to generate color scale
const generateColorScale = (baseColor: string): string[] => {
	const { h, s, l } = hexToHsl(baseColor)
	const scale: string[] = []

	// Generate 10 variations from dark to light
	for (let i = 0; i < 10; i++) {
		const lightness = (10 + i * 9) / 100 // From 0.10 to 0.91 (10% to 91%)
		scale.push(hslToHex(h, s, lightness))
	}

	return scale
}

// Helper function to ensure a color is dark enough for contrast
const ensureDarkColor = (color: string): string => {
	const { h, s, l } = hexToHsl(color)
	// If color is too light, darken it
	if (l > 40) {
		return hslToHex(h, s, 25)
	}
	return color
}

const executeDeleteTheme = async () => {
	if (!selectedThemeToDelete.value?.id) return

	isDeletingTheme.value = true

	try {
		// Get current theme to check if we need to switch to default after deletion
		const deletingCurrentTheme = selectedTemplateId.value === selectedThemeToDelete.value.id

		// Call API to soft delete the theme (set isActive = 0)
		await themeMgmClient.deleteTheme(selectedThemeToDelete.value.id)

		// Refresh templates list from server (it will exclude the soft-deleted theme)
		await loadTemplatesFromDB()

		// If we deleted the currently selected theme, switch to default
		if (deletingCurrentTheme) {
			const defaultTheme = templates.value.find((t) => t.isDefaultTemplate)
			if (defaultTheme) {
				selectedTemplateId.value = defaultTheme.id
				await loadTemplateFromDB()
			} else {
				// Fallback: select first available theme
				if (templates.value.length > 0) {
					selectedTemplateId.value = templates.value[0].id
					await loadTemplateFromDB()
				}
			}
		}

		toast.add({
			severity: "success",
			summary: "Theme Deleted",
			detail: `Theme "${selectedThemeToDelete.value.templateName}" has been successfully deleted.`,
			life: 3000,
		})

		closeDeleteConfirmDialog()
	} catch (error) {
		console.error("Error deleting theme:", error)
		toast.add({
			severity: "error",
			summary: "Delete Failed",
			detail: "Failed to delete the theme. Please try again.",
			life: 3000,
		})
	} finally {
		isDeletingTheme.value = false
	}
}

// Export Theme Function
const exportTheme = async () => {
	if (!selectedTemplateId.value) {
		toast.add({
			severity: "error",
			summary: "Export Error",
			detail: "No theme selected for export.",
			life: 3000,
		})
		return
	}

	try {
		// Test API connectivity first
		const testResult = await themeMgmClient.getAllThemes()

		const fileResponse = await themeMgmClient.exportTheme(selectedTemplateId.value)
		const blob = fileResponse.data

		if (blob.size === 0) {
			throw new Error("Empty file received from server")
		}

		// Get the selected theme name for the filename
		const selectedTheme = templates.value.find((t) => t.id === selectedTemplateId.value)
		const themeName = selectedTheme?.templateName || "theme"

		// Create safe filename by removing special characters
		const safeThemeName = themeName.replace(/[^a-zA-Z0-9-_]/g, "_")
		const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, -5)
		const fileName = `${safeThemeName}_${timestamp}.lcat`

		// Validate file size (should be reasonable for a theme)
		if (blob.size < 100) {
			throw new Error("Export file appears to be too small to be valid")
		}

		if (blob.size > 10 * 1024 * 1024) {
			// 10MB limit
			throw new Error("Export file is unexpectedly large")
		}

		// Create download link
		const url = window.URL.createObjectURL(blob)
		const link = document.createElement("a")
		link.href = url
		link.download = fileName
		link.style.display = "none"
		document.body.appendChild(link)
		link.click()

		// Clean up
		setTimeout(() => {
			document.body.removeChild(link)
			window.URL.revokeObjectURL(url)
		}, 100)

		toast.add({
			severity: "success",
			summary: "Export Successful",
			detail: `Theme "${themeName}" has been exported as "${fileName}".`,
			life: 5000,
		})
	} catch (error: any) {
		let errorMessage = "Failed to export the theme. Please try again."

		if (error.message.includes("too small")) {
			errorMessage = "Export failed: The exported file appears to be corrupted or empty."
		} else if (error.message.includes("too large")) {
			errorMessage = "Export failed: The theme data is unexpectedly large."
		} else if (error.message.includes("network") || error.message.includes("fetch")) {
			errorMessage = "Network error occurred. Please check your connection and try again."
		}

		toast.add({
			severity: "error",
			summary: "Export Failed",
			detail: errorMessage,
			life: 5000,
		})
	}
}

// Trigger Import File Dialog
const triggerImportFile = () => {
	importFileInput.value?.click()
}

// Handle Import File Selection
const handleImportFile = async (event: Event) => {
	const target = event.target as HTMLInputElement
	const file = target.files?.[0]

	if (!file) return

	// Validate file type
	if (!file.name.toLowerCase().endsWith(".lcat")) {
		toast.add({
			severity: "error",
			summary: "Invalid File Type",
			detail: "Please select an LCAT file exported from this application.",
			life: 5000,
		})
		target.value = "" // Clear the input
		return
	}

	// Validate file size (10MB limit)
	if (file.size > 10 * 1024 * 1024) {
		toast.add({
			severity: "error",
			summary: "File Too Large",
			detail: "The selected file exceeds the 10MB limit.",
			life: 5000,
		})
		target.value = "" // Clear the input
		return
	}

	// Check if file is too small to be valid
	if (file.size < 100) {
		toast.add({
			severity: "error",
			summary: "Invalid File",
			detail: "The selected file appears to be too small to contain valid theme data.",
			life: 5000,
		})
		target.value = "" // Clear the input
		return
	}

	selectedImportFile.value = file
	await attemptImport(false)

	// Clear the input so the same file can be selected again if needed
	target.value = ""
}

// Attempt Import (with optional confirmation)
const attemptImport = async (confirmOverwrite: boolean) => {
	if (!selectedImportFile.value || !selectedTemplateId.value) {
		toast.add({
			severity: "error",
			summary: "Import Error",
			detail: "No file selected or no target theme selected.",
			life: 3000,
		})
		return
	}

	try {
		isImporting.value = true

		// Get current theme info for better error messages
		const currentTheme = templates.value.find((t) => t.id === selectedTemplateId.value)

		const fileParameter: FileParameter = {
			data: selectedImportFile.value,
			fileName: selectedImportFile.value.name,
		}

		await themeMgmClient.importTheme(selectedTemplateId.value, confirmOverwrite ? "true" : undefined, fileParameter)

		toast.add({
			severity: "success",
			summary: "Import Successful",
			detail: `Theme has been imported successfully into "${currentTheme?.templateName || "selected theme"}".`,
			life: 5000,
		})

		// Reload the current theme to show the changes
		await loadTemplateFromDB()

		// Reset import state
		selectedImportFile.value = null
		showImportConfirmDialog.value = false
	} catch (error: any) {
		// The importTheme endpoint only declares a bare 409 status code with no response
		// schema, so the generated client throws the parsed conflict body directly instead
		// of wrapping it in an APIException. The 400/404 responses do go through APIException,
		// whose "response" property may already be a parsed object (JSON content-type is
		// auto-parsed by axios) or a raw string, depending on the response, so both are handled.
		let status: number | undefined
		let data: any = {}

		if (APIException.isAPIException(error)) {
			status = error.status
			if (error.response) {
				if (typeof error.response === "string") {
					try {
						data = JSON.parse(error.response)
					} catch {
						data = {}
					}
				} else {
					data = error.response
				}
			}
		} else if (error && typeof error === "object" && "requiresConfirmation" in error) {
			data = error
			status = 409
		}

		if (status === 409) {
			// Show confirmation dialog when target theme is newer than imported theme
			if (data.requiresConfirmation && data.warningType === "NEWER_TARGET_THEME") {
				// Format timestamps for better readability
				const currentDate = new Date(data.currentTimestamp).toLocaleString()
				const importedDate = new Date(data.importedTimestamp).toLocaleString()

				// Store additional information for the dialog
				importDialogData.value.currentTimestamp = currentDate
				importDialogData.value.importedTimestamp = importedDate
				importDialogData.value.timeDifference = data.timeDifferenceInDays || 0
				importDialogData.value.currentThemeName = data.currentThemeName || "current theme"
				importDialogData.value.detailMessage = data.detailMessage || ""

				showImportConfirmDialog.value = true
				return // Don't show error toast, show dialog instead
			}
		} else if (status === 400) {
			// Handle specific 400 errors
			let errorDetail = "The import file appears to be corrupted or invalid."

			if (data.message?.includes("Invalid theme file")) {
				errorDetail = "The LCAT file does not contain valid theme data (missing theme.json)."
			} else if (data.message?.includes("File size exceeds")) {
				errorDetail = "The import file is too large (max 10MB allowed)."
			} else if (data.message?.includes("No file uploaded")) {
				errorDetail = "No file was received by the server."
			}

			toast.add({
				severity: "error",
				summary: "Import Failed",
				detail: errorDetail,
				life: 5000,
			})
		} else if (status === 404) {
			toast.add({
				severity: "error",
				summary: "Import Failed",
				detail: "The target theme was not found. Please select a valid theme.",
				life: 5000,
			})
		} else if (error.message?.includes("network") || error.message?.includes("fetch")) {
			toast.add({
				severity: "error",
				summary: "Import Failed",
				detail: "Network error occurred. Please check your connection and try again.",
				life: 5000,
			})
		} else {
			// Generic error
			const errorDetail = data.message || error.message || "An unexpected error occurred."
			toast.add({
				severity: "error",
				summary: "Import Failed",
				detail: `Failed to import theme: ${errorDetail}`,
				life: 5000,
			})
		}
	} finally {
		isImporting.value = false
	}
}

// Confirm Import (when user clicks "Import Anyway")
const confirmImport = async () => {
	await attemptImport(true)
}

// Cancel Import
const cancelImport = () => {
	selectedImportFile.value = null
	showImportConfirmDialog.value = false
	isImporting.value = false

	// Reset dialog data
	importDialogData.value = {
		currentTimestamp: "",
		importedTimestamp: "",
		timeDifference: 0,
		currentThemeName: "",
		detailMessage: "",
	}

	// Clear any file input if it exists
	if (importFileInput.value) {
		importFileInput.value.value = ""
	}
}

// Function to generate semantic colors based on color theory
const generateSemanticColors = (baseColor: string, schemeType: string): { primary: string; secondary: string; accent: string } => {
	const { h, s, l } = hexToHsl(baseColor)

	switch (schemeType) {
		case "monochromatic":
			return {
				primary: baseColor,
				secondary: hslToHex(h, Math.max(s - 0.2, 0.2), Math.max(l - 0.15, 0.2)),
				accent: hslToHex(h, Math.min(s + 0.1, 1.0), Math.min(l + 0.2, 0.9)),
			}

		case "analogous":
			return {
				primary: baseColor,
				secondary: hslToHex((h + 30) % 360, s, Math.max(l - 0.1, 0.2)),
				accent: hslToHex((h - 30 + 360) % 360, s, Math.min(l + 0.1, 0.9)),
			}

		case "complementary":
			return {
				primary: baseColor,
				secondary: hslToHex((h + 180) % 360, s, l),
				accent: hslToHex((h + 180) % 360, Math.max(s - 0.3, 0.3), Math.min(l + 0.2, 0.8)),
			}

		case "triadic":
			return {
				primary: baseColor,
				secondary: hslToHex((h + 120) % 360, s, l),
				accent: hslToHex((h + 240) % 360, s, l),
			}

		case "splitComplementary":
			return {
				primary: baseColor,
				secondary: hslToHex((h + 150) % 360, s, Math.max(l - 0.1, 0.2)),
				accent: hslToHex((h + 210) % 360, s, Math.min(l + 0.1, 0.9)),
			}

		case "tetradic":
			return {
				primary: baseColor,
				secondary: hslToHex((h + 90) % 360, s, l),
				accent: hslToHex((h + 270) % 360, s, l),
			}

		case "business":
			// Professional business scheme - conservative colors
			return {
				primary: hslToHex(h, Math.min(s * 0.7, 0.6), Math.min(Math.max(l, 0.3), 0.7)), // More muted primary
				secondary: hslToHex(210, 0.5, 0.4), // Professional blue-gray
				accent: hslToHex((h + 45) % 360, Math.min(s * 0.8, 0.7), Math.min(l + 0.1, 0.8)), // Subtle accent
			}

		case "creative":
			// Creative/artistic scheme - bold and vibrant
			return {
				primary: hslToHex(h, Math.min(s * 1.2, 1.0), Math.max(Math.min(l, 0.8), 0.4)), // Vibrant primary
				secondary: hslToHex((h + 140) % 360, Math.min(s * 1.1, 1.0), Math.max(l - 0.2, 0.2)), // Bold secondary
				accent: hslToHex((h + 220) % 360, Math.min(s * 1.15, 1.0), Math.min(l + 0.3, 0.9)), // Bright accent
			}

		default:
			// Fallback to analogous
			return {
				primary: baseColor,
				secondary: hslToHex((h + 30) % 360, s, Math.max(l - 0.1, 0.2)),
				accent: hslToHex((h - 30 + 360) % 360, s, Math.min(l + 0.1, 0.9)),
			}
	}
}

// Add event listener to exit selection mode with ESC
const handleKeydown = (event: KeyboardEvent) => {
	if (event.key === "Escape") {
		exitColorSelectionMode()
		hideColorPicker()
	}
}

// Add event listener to hide picker when clicking elsewhere
const handleDocumentClick = (event: MouseEvent) => {
	const target = event.target as HTMLElement
	if (!target.closest(".color-item-vertical")) {
		hideColorPicker()
	}
	if (!target.closest(".quick-color-item")) {
		showQuickPicker.value = null
	}
}

// Variable to store theme before picker opens
let themeBeforePickerOpen: any = null

onMounted(async () => {
	await loadTemplatesFromDB()

	// Add event listeners
	document.addEventListener("keydown", handleKeydown)
	document.addEventListener("click", handleDocumentClick)
})

// Cleanup event listeners
onUnmounted(() => {
	document.removeEventListener("keydown", handleKeydown)
	document.removeEventListener("click", handleDocumentClick)
	document.body.style.cursor = "default" // Reset cursor
})
</script>

<style scoped>
/* Tab list with undo button styles */
.tab-list-with-undo {
	display: flex !important;
	align-items: center !important;
	justify-content: space-between !important;
	width: 100% !important;
}

.tab-list-with-undo .p-tab {
	flex: 0 0 auto;
}

.undo-button-inline {
	margin-left: auto !important;
	margin-right: 1rem !important;
}
/* Create Theme Button */
.create-theme-button {
	background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%) !important;
	border: none !important;
	color: white !important;
	padding: 0.5rem 0.75rem !important;
	border-radius: 8px !important;
	font-weight: 600 !important;
	transition: all 0.3s ease !important;
	box-shadow: 0 2px 8px rgba(107, 114, 128, 0.2) !important;
}

.create-theme-button:hover:not(:disabled) {
	background: linear-gradient(135deg, #4b5563 0%, #374151 100%) !important;
	transform: translateY(-2px) !important;
	box-shadow: 0 4px 12px rgba(107, 114, 128, 0.3) !important;
}

.create-theme-button:disabled {
	background: #9ca3af !important;
	color: #6b7280 !important;
	cursor: not-allowed !important;
	transform: none !important;
	box-shadow: none !important;
}

/* Create Theme Dialog */
.create-theme-content {
	padding: 1rem 0;
}

.field {
	margin-bottom: 1.5rem;
}

.field-label {
	display: block;
	margin-bottom: 0.5rem;
	font-weight: 600;
	color: #374151;
}

/* Header Section - Sticky */
.header-section {
	position: sticky;
	top: 64px;
	z-index: 100;
	margin-bottom: 24px;
	background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
	border-radius: 12px;
	border: 1px solid #e9ecef;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
	padding: 16px 24px;
	backdrop-filter: blur(10px);
	-webkit-backdrop-filter: blur(10px);
}

.controls-section {
	display: flex;
	justify-content: flex-start;
	align-items: center;
	width: 100%;
}

.control-group {
	display: flex;
	flex-direction: column;
	gap: 12px;
	width: 100%;
}

.control-row {
	display: flex;
	align-items: center;
	gap: 8px;
	width: 100%;
	flex-wrap: nowrap;
	overflow: visible;
}

.right-actions {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-left: auto;
	flex-shrink: 0;
}

.checkbox-wrapper {
	display: flex;
	align-items: center;
	gap: 8px;
	background: rgba(255, 255, 255, 0.8);
	padding: 8px 12px;
	border-radius: 8px;
	border: 1px solid rgba(233, 236, 239, 0.5);
	transition: all 0.3s ease;
	white-space: nowrap;
	flex-shrink: 0;
}

.checkbox-wrapper:hover {
	background: rgba(255, 255, 255, 1);
	border-color: #007bff;
	box-shadow: 0 2px 8px rgba(0, 123, 255, 0.1);
}

.checkbox-wrapper label {
	font-size: 13px;
	font-weight: 500;
	color: #495057;
	cursor: pointer;
	margin: 0;
}

.select-template {
	min-width: 200px;
	max-width: 280px;
	flex: 0 1 auto;
}

.save-button {
	background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
	border: none;
	border-radius: 8px;
	padding: 8px 16px;
	font-weight: 500;
	font-size: 13px;
	color: white;
	box-shadow: 0 2px 8px rgba(0, 123, 255, 0.2);
	transition: all 0.3s ease;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	white-space: nowrap;
	flex-shrink: 0;
}

.save-button:hover {
	transform: translateY(-1px);
	box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
	background: linear-gradient(135deg, #0056b3 0%, #004085 100%);
}

.save-button i {
	font-size: 14px;
}

/* Main Layout */
.main-layout {
	display: flex;
	gap: 32px;
	min-height: 100vh;
}

/* Color Sidebar */
.color-sidebar {
	width: 320px;
	flex-shrink: 0;
	background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
	border-radius: 12px;
	border: 1px solid #e9ecef;
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
	padding: 16px;
	height: fit-content;
	position: sticky;
	top: 24px;
}

.sidebar-title {
	font-size: 16px;
	font-weight: 700;
	color: #495057;
	margin: 0 0 16px 0;
	text-align: center;
	padding-bottom: 12px;
	border-bottom: 2px solid #e9ecef;
}

/* Vertical Color Grid */
.color-palette-section {
	margin-bottom: 16px;
}

/* Palette Controls */
.palette-controls {
	display: flex;
	justify-content: center;
	margin-bottom: 12px;
}

.reset-palette-button {
	background: linear-gradient(135deg, #6c757d 0%, #5a6268 100%);
	border: none;
	border-radius: 8px;
	padding: 6px 12px;
	font-weight: 500;
	font-size: 12px;
	color: white;
	box-shadow: 0 2px 6px rgba(108, 117, 125, 0.2);
	transition: all 0.3s ease;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	white-space: nowrap;
}

.reset-palette-button:hover {
	transform: translateY(-1px);
	box-shadow: 0 4px 10px rgba(108, 117, 125, 0.3);
	background: linear-gradient(135deg, #5a6268 0%, #495057 100%);
}

.reset-palette-button i {
	font-size: 12px;
}

.color-grid-vertical {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.color-item-vertical {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 6px;
	padding: 8px;
	border-radius: 8px;
	transition: all 0.3s ease;
	background: rgba(255, 255, 255, 0.7);
	border: 1px solid rgba(233, 236, 239, 0.5);
}

/* Semantic colors container with unified border */
.semantic-colors-container {
	border: 4px solid #000000;
	border-radius: 12px;
	padding: 16px;
	margin-bottom: 20px;
	background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
	box-shadow:
		0 6px 20px rgba(0, 0, 0, 0.15),
		0 3px 10px rgba(0, 0, 0, 0.1);
	position: relative;
	overflow: visible;
	min-height: fit-content;
	width: 100%;
}

.semantic-colors-grid {
	display: flex;
	flex-direction: column;
	gap: 8px;
	position: relative;
	z-index: 1;
}

/* Semantic color items - simpler styling since they're inside the container */
.semantic-colors-container .color-item-vertical {
	background: rgba(255, 255, 255, 0.8);
	border: 1px solid rgba(0, 0, 0, 0.1);
	box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
	padding: 8px;
	margin-bottom: 0;
	position: relative;
}

.semantic-colors-container .color-item-vertical:hover {
	background: rgba(255, 255, 255, 1);
	border-color: #007bff;
	box-shadow: 0 4px 12px rgba(0, 123, 255, 0.2);
	transform: translateX(4px);
}

/* Apply Semantic Colors Button */
.apply-semantic-colors-button-container {
	margin-top: 12px;
	padding-top: 12px;
	border-top: 1px solid rgba(0, 0, 0, 0.1);
	display: flex;
	justify-content: center;
}

.apply-semantic-colors-button {
	background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%) !important;
	border: none !important;
	color: white !important;
	padding: 0.5rem 1rem !important;
	border-radius: 8px !important;
	font-weight: 600 !important;
	transition: all 0.3s ease !important;
	box-shadow: 0 2px 8px rgba(79, 70, 229, 0.3) !important;
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.5rem;
}

.apply-semantic-colors-button:hover {
	transform: translateY(-2px) !important;
	box-shadow: 0 4px 12px rgba(79, 70, 229, 0.4) !important;
	background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%) !important;
}

/* Apply Semantic Colors Dialog */
.apply-semantic-colors-dialog .current-colors-display {
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
	border-radius: 8px;
	border: 1px solid #e5e7eb;
}

.apply-semantic-colors-dialog .colors-preview {
	display: flex;
	gap: 1rem;
	margin-top: 0.5rem;
}

.apply-semantic-colors-dialog .color-preview-item {
	flex: 1;
	display: flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.5rem;
	background: white;
	border-radius: 6px;
	border: 1px solid #e5e7eb;
}

.apply-semantic-colors-dialog .color-swatch-small {
	width: 32px;
	height: 32px;
	border-radius: 4px;
	border: 2px solid #fff;
	box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.apply-semantic-colors-dialog .color-preview-item span {
	font-size: 0.85rem;
	color: #374151;
	font-weight: 500;
}

.apply-semantic-colors-dialog .strategy-explanation {
	margin-top: 1.5rem;
	animation: slideDown 0.3s ease-out;
}

/* Regular colors grid */
.regular-colors-grid {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.color-item-row {
	display: flex;
	align-items: center;
	gap: 8px;
	width: 100%;
}

.color-item-vertical:hover {
	background: rgba(255, 255, 255, 1);
	border-color: #007bff;
	box-shadow: 0 4px 12px rgba(0, 123, 255, 0.1);
	transform: translateX(4px);
}

.color-number-label {
	font-weight: 700;
	font-size: 14px;
	color: #6c757d;
	background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
	width: 32px;
	height: 32px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	border: 1px solid #e9ecef;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
	transition: all 0.3s ease;
	flex-shrink: 0;
}

.color-item-vertical:hover .color-number-label {
	border-color: #007bff;
	color: #007bff;
	transform: scale(1.1);
}

/* Color Swatch Wrapper */
.color-swatch-wrapper {
	position: relative;
	display: inline-block;
}

.color-swatch {
	width: 40px;
	height: 40px;
	border-radius: 8px;
	border: 2px solid #ffffff;
	box-shadow:
		0 2px 8px rgba(0, 0, 0, 0.08),
		0 0 0 1px rgba(0, 0, 0, 0.04);
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	cursor: pointer;
	position: relative;
}

.color-swatch:hover {
	transform: scale(1.05);
	box-shadow:
		0 8px 24px rgba(0, 0, 0, 0.12),
		0 0 0 1px rgba(0, 0, 0, 0.08);
}

/* Color selezionato con click sinistro (modalità mirino) */
.color-swatch.selected-crosshair {
	border: 4px solid #007bff;
	box-shadow:
		0 0 0 2px #ffffff,
		0 0 0 4px #007bff,
		0 8px 24px rgba(0, 123, 255, 0.3);
	transform: scale(1.1);
}

.color-swatch.selected-crosshair::after {
	content: "✓";
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	color: white;
	font-weight: bold;
	font-size: 18px;
	text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
}

/* Color selezionato con click destro (modalità picker) */
.color-swatch.selected-picker {
	border: 3px solid #007bff;
	box-shadow:
		0 0 0 1px #ffffff,
		0 0 0 3px #007bff,
		0 4px 12px rgba(0, 123, 255, 0.2);
	transform: scale(1.05);
}

.color-picker-container {
	position: absolute;
	top: 0;
	left: 100%;
	z-index: 1000;
	margin-left: 16px;
	pointer-events: auto;
	background: white;
	border: 2px solid #007bff;
	border-radius: 12px;
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
	padding: 12px;
	min-width: 200px;
}

.color-picker-container-below {
	position: relative;
	width: 100%;
	margin-top: 12px;
	z-index: 1000;
	pointer-events: auto;
	background: #ffffff;
	border: 2px solid #dee2e6;
	border-radius: 8px;
	padding: 16px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.picker-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 16px;
	padding-bottom: 12px;
	border-bottom: 2px solid #f0f2f5;
	font-size: 14px;
	font-weight: 700;
	color: #495057;
	background: linear-gradient(90deg, #007bff 0%, #0056b3 100%);
	background-clip: text;
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
}

.close-picker {
	background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
	border: none;
	cursor: pointer;
	font-size: 16px;
	color: white;
	padding: 0;
	width: 28px;
	height: 28px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 50%;
	transition: all 0.3s ease;
	box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
	font-weight: bold;
}

.close-picker:hover {
	background: linear-gradient(135deg, #c82333 0%, #a71e2a 100%);
	transform: scale(1.1);
	box-shadow: 0 4px 12px rgba(220, 53, 69, 0.4);
}

.color-picker-numbered {
	pointer-events: auto;
	border-radius: 6px;
	overflow: hidden;
	margin-bottom: 12px;
	box-shadow: none;
}

.color-picker-numbered ::v-deep(.p-colorpicker-preview) {
	display: none;
}

.color-picker-numbered ::v-deep(.p-colorpicker-panel) {
	border: none !important;
	border-radius: 6px !important;
	box-shadow: none !important;
}

.color-swatch-controls {
	display: flex;
	align-items: center;
	gap: 8px;
	position: relative;
}

.color-edit-button {
	background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
	border: 1px solid #dee2e6;
	border-radius: 6px;
	padding: 6px;
	width: 28px;
	height: 28px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: all 0.3s ease;
	color: #6c757d;
	flex-shrink: 0;
}

.color-edit-button:hover {
	background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
	border-color: #007bff;
	color: white;
	transform: scale(1.1);
	box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
}

.color-edit-button.active {
	background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
	border-color: #007bff;
	color: white;
	box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
}

.color-edit-button i {
	font-size: 12px;
}

/* Color gradations button styles */
.color-gradations-button {
	background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
	border: 1px solid #28a745;
	border-radius: 6px;
	padding: 6px;
	width: 28px;
	height: 28px;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.2s ease;
	color: white;
	margin-left: 4px;
}

.color-gradations-button:hover {
	background: linear-gradient(135deg, #20c997 0%, #17a2b8 100%);
	border-color: #20c997;
	transform: scale(1.1);
	box-shadow: 0 2px 8px rgba(32, 201, 151, 0.3);
}

.color-gradations-button.active {
	background: linear-gradient(135deg, #17a2b8 0%, #138496 100%);
	border-color: #17a2b8;
	box-shadow: 0 2px 8px rgba(23, 162, 184, 0.3);
}

.color-gradations-button i {
	font-size: 12px;
}

/* Gradations overlay styles */
.gradations-overlay {
	position: fixed !important;
	top: 50% !important;
	left: 50% !important;
	transform: translate(-50%, -50%) !important;
	z-index: 999999 !important;
	background: red !important;
	border: 5px solid blue !important;
	border-radius: 8px;
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
	padding: 16px;
	width: 320px;
	max-height: 400px;
	overflow-y: auto;
}

.gradations-container {
	min-width: 300px;
}

.gradations-title {
	font-size: 14px;
	font-weight: 600;
	margin: 0 0 12px 0;
	color: #495057;
}

.gradations-grid {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 8px;
}

.gradation-item {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 4px;
	border-radius: 4px;
	background: #f8f9fa;
}

.gradation-swatch {
	width: 24px;
	height: 24px;
	border-radius: 4px;
	border: 1px solid #dee2e6;
	flex-shrink: 0;
}

.gradation-label {
	font-size: 11px;
	font-family: "Courier New", monospace;
	color: #6c757d;
}

.hex-input-container {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 12px;
	padding: 12px;
	background: #f8f9fa;
	border: 1px solid #e9ecef;
	border-radius: 6px;
}

.hex-input-label {
	font-size: 13px;
	font-weight: 600;
	color: #495057;
	min-width: 35px;
	background: linear-gradient(90deg, #007bff 0%, #0056b3 100%);
	background-clip: text;
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
}

.hex-input {
	flex: 1;
	font-family: "SF Mono", "Monaco", "Inconsolata", "Roboto Mono", monospace;
	font-size: 12px;
	font-weight: 500;
	text-transform: uppercase;
	padding: 6px 8px;
	border: 1px solid #e9ecef;
	border-radius: 4px;
	background: white;
	transition: all 0.2s ease;
}

.hex-input:focus {
	border-color: #007bff;
	box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
	outline: none;
}

.send-color-btn {
	min-width: 40px !important;
	height: 40px !important;
	padding: 0 !important;
	border-radius: 10px !important;
	background: linear-gradient(135deg, #28a745 0%, #20c997 100%) !important;
	color: white !important;
	border: none !important;
	box-shadow:
		0 4px 12px rgba(40, 167, 69, 0.3),
		0 1px 3px rgba(0, 0, 0, 0.1) !important;
	transition: all 0.3s ease !important;
	position: relative;
	overflow: hidden;
}

.send-color-btn:hover {
	background: linear-gradient(135deg, #20c997 0%, #17a2b8 100%) !important;
	transform: translateY(-2px) scale(1.05) !important;
	box-shadow:
		0 6px 20px rgba(40, 167, 69, 0.4),
		0 2px 8px rgba(0, 0, 0, 0.15) !important;
}

.send-color-btn:active {
	transform: translateY(-1px) scale(1.02) !important;
	transition: all 0.1s ease !important;
}

.send-color-btn i {
	font-size: 16px !important;
	font-weight: bold;
}

.color-special-label {
	font-size: 14px;
	font-weight: bold;
	color: #000000;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	min-width: 90px;
	display: flex;
	align-items: center;
	margin-right: 12px;
}

/* Simple spacing between semantic and regular colors */
.colors-spacing {
	height: 20px;
}

/* Label always visible for semantic colors */
.semantic-color-row {
	display: flex;
	align-items: center;
	justify-content: flex-start;
}

.semantic-color-row .color-swatch-controls {
	display: flex;
	align-items: center;
	gap: 8px;
	position: relative;
}

/* Sidebar Controls */
.controls-section-sidebar {
	border-top: 2px solid #e9ecef;
	padding-top: 24px;
}

.control-group-sidebar {
	display: flex;
	flex-direction: column;
	gap: 20px;
}

/* Content Area */
.content-area {
	flex: 1;
	min-width: 0;
}

/* Responsive Design */
@media (max-width: 1200px) {
	.color-sidebar {
		width: 320px;
	}

	.main-layout {
		gap: 24px;
	}
}

@media (max-width: 768px) {
	.header-section {
		padding: 12px 16px;
	}

	.control-group {
		flex-direction: column;
		align-items: stretch;
		gap: 12px;
	}

	.checkbox-wrapper {
		justify-content: center;
	}

	.select-template {
		min-width: auto;
		max-width: none;
	}

	.save-button {
		justify-content: center;
	}
}

@media (max-width: 1024px) {
	.main-layout {
		flex-direction: column;
		gap: 24px;
	}

	.color-sidebar {
		width: 100%;
		position: static;
		height: auto;
	}

	.color-grid-vertical {
		flex-direction: row;
		flex-wrap: wrap;
		justify-content: center;
		gap: 16px;
	}

	.color-item-vertical {
		flex-direction: column;
		text-align: center;
		min-width: 100px;
	}

	/* Label alignment maintained on desktop */
}

@media (max-width: 768px) {
	.color-swatch {
		width: 48px;
		height: 48px;
	}

	.color-swatch.selected-crosshair::after {
		font-size: 16px;
	}

	.color-picker-numbered {
		top: 52px;
	}

	.color-number-label {
		width: 28px;
		height: 28px;
		font-size: 12px;
	}
}

@media (max-width: 480px) {
	.color-sidebar {
		padding: 16px;
	}

	.main-layout {
		gap: 16px;
	}
}

/* Vertical Divider */
.vertical-divider {
	width: 2px;
	height: 40px;
	background: linear-gradient(180deg, rgba(233, 236, 239, 0.3) 0%, rgba(108, 117, 125, 0.8) 50%, rgba(233, 236, 239, 0.3) 100%);
	border-radius: 1px;
	margin: 0 12px;
	flex-shrink: 0;
	box-shadow:
		0 1px 3px rgba(0, 0, 0, 0.1),
		inset 0 1px 1px rgba(255, 255, 255, 0.3);
}

/* Quick Color Pickers Styles */
.quick-color-pickers {
	display: flex;
	gap: 16px;
	align-items: center;
	margin-left: 0;
}

.quick-color-item {
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 6px;
}

.quick-color-swatch {
	width: 48px;
	height: 48px;
	border-radius: 12px;
	border: 3px solid #ffffff;
	box-shadow:
		0 4px 12px rgba(0, 0, 0, 0.12),
		0 0 0 1px rgba(0, 0, 0, 0.06);
	cursor: pointer;
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
}

.quick-color-swatch:hover {
	transform: scale(1.15);
	box-shadow:
		0 8px 24px rgba(0, 0, 0, 0.15),
		0 0 0 1px rgba(0, 0, 0, 0.1);
}

.quick-color-description {
	font-size: 12px;
	font-weight: 600;
	color: #495057;
	text-transform: capitalize;
	letter-spacing: 0.3px;
	white-space: nowrap;
	background: linear-gradient(135deg, #6c757d 0%, #495057 100%);
	background-clip: text;
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	transition: all 0.3s ease;
	text-align: center;
	min-height: 16px;
}

.quick-color-item:hover .quick-color-description {
	background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
	background-clip: text;
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	transform: translateY(-1px);
}

.quick-color-picker-container {
	position: absolute;
	top: 100%;
	left: 50%;
	transform: translateX(-50%);
	z-index: 1000;
	margin-top: 8px;
	background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
	border: 1px solid #e3e6ea;
	border-radius: 12px;
	box-shadow:
		0 8px 32px rgba(0, 0, 0, 0.15),
		0 2px 8px rgba(0, 123, 255, 0.08);
	padding: 16px;
	animation: quickPickerSlideDown 0.25s ease-out;
	border-left: 3px solid #007bff;
	min-width: 200px;
}

@keyframes quickPickerSlideDown {
	from {
		opacity: 0;
		transform: translateX(-50%) translateY(-8px);
	}
	to {
		opacity: 1;
		transform: translateX(-50%) translateY(0);
	}
}

.quick-color-picker {
	border-radius: 8px;
	overflow: hidden;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.quick-color-picker ::v-deep(.p-colorpicker-preview) {
	display: none;
}

.quick-color-picker ::v-deep(.p-colorpicker-panel) {
	border: none !important;
	border-radius: 8px !important;
	box-shadow: none !important;
}

/* Responsive adjustments for quick color pickers */
@media (max-width: 768px) {
	.vertical-divider {
		height: 32px;
		margin: 0 8px;
	}

	.quick-color-pickers {
		margin-left: 0;
		gap: 12px;
		flex-wrap: wrap;
	}

	.quick-color-swatch {
		width: 40px;
		height: 40px;
		border-radius: 10px;
	}

	.quick-color-description {
		font-size: 11px;
	}

	.quick-color-picker-container {
		min-width: 180px;
		padding: 12px;
	}
}

/* Standard Theme Warning Styles */
.standard-theme-warning {
	background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
	border: 2px solid #ffc107;
	border-radius: 12px;
	margin-bottom: 24px;
	padding: 16px 20px;
	box-shadow: 0 4px 12px rgba(255, 193, 7, 0.2);
}

.warning-content {
	display: flex;
	align-items: center;
	gap: 12px;
	flex-wrap: wrap;
}

.warning-icon {
	color: #dc3545;
	font-size: 20px;
	flex-shrink: 0;
}

.warning-text {
	color: #856404;
	font-weight: 600;
	font-size: 14px;
	flex: 1;
	min-width: 250px;
}

.warning-controls {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-left: auto;
}

.override-checkbox {
	display: flex;
	align-items: center;
	gap: 6px;
	cursor: pointer;
	font-size: 13px;
	font-weight: 500;
	color: #495057;
	white-space: nowrap;
}

.override-checkbox:hover {
	color: #007bff;
}

/* Disabled Save Button */
.save-button.disabled {
	background: linear-gradient(135deg, #6c757d 0%, #5a6268 100%) !important;
	cursor: not-allowed !important;
	opacity: 0.6 !important;
	box-shadow: none !important;
}

.save-button.disabled:hover {
	transform: none !important;
	background: linear-gradient(135deg, #6c757d 0%, #5a6268 100%) !important;
	box-shadow: none !important;
}

/* Color Scheme Dialog Styles */
.color-scheme-dialog {
	z-index: 10000 !important;
}

/* Ensure dialog overlay is also on top */
:deep(.p-dialog-mask) {
	z-index: 9999 !important;
}

/* Lower the z-index of PrimeVue ColorPicker panels to ensure dialogs appear on top */
:deep(.p-colorpicker-panel) {
	z-index: 1100 !important;
}

/* Gradient Picker Button Styles */
.gradient-picker-controls {
	display: flex;
	justify-content: center;
	margin-top: 8px;
	padding-top: 8px;
	border-top: 1px solid #e5e7eb;
}

.gradient-picker-button {
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 6px 12px;
	background: #f8f9fa;
	border: 1px solid #dee2e6;
	border-radius: 6px;
	color: #495057;
	font-size: 12px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;
}

.gradient-picker-button:hover {
	background: #e9ecef;
	border-color: #adb5bd;
	color: #343a40;
	transform: translateY(-1px);
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.gradient-picker-button:active {
	transform: translateY(0px);
	box-shadow: none;
}

.gradient-picker-button i {
	font-size: 11px;
}

.color-scheme-content {
	padding: 1rem 0;
}

.base-color-display {
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: #f8f9fa;
	border-radius: 8px;
	border: 1px solid #e9ecef;
}

.base-color-preview {
	display: flex;
	align-items: center;
	gap: 12px;
	margin-top: 0.5rem;
}

.color-preview-swatch {
	width: 40px;
	height: 40px;
	border-radius: 8px;
	border: 2px solid #ffffff;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.color-hex-value {
	font-family: "SF Mono", "Monaco", "Inconsolata", "Roboto Mono", monospace;
	font-weight: 600;
	font-size: 14px;
	color: #495057;
	background: #ffffff;
	padding: 6px 10px;
	border-radius: 4px;
	border: 1px solid #e9ecef;
}

.color-scheme-dialog .field-label {
	font-weight: 600;
	color: #374151;
	margin-bottom: 0.5rem;
	display: block;
}

.color-scheme-dialog .p-help {
	color: #6c757d;
	font-style: italic;
	margin-top: 0.5rem;
	line-height: 1.4;
}

/* Color Scheme Generator Button Styles */
.color-scheme-generator-button {
	background: linear-gradient(135deg, #28a745 0%, #20c997 100%) !important;
	border: none !important;
	border-radius: 8px !important;
	padding: 8px 16px !important;
	font-weight: 500 !important;
	font-size: 13px !important;
	color: white !important;
	box-shadow: 0 2px 8px rgba(40, 167, 69, 0.2) !important;
	transition: all 0.3s ease !important;
	display: flex !important;
	align-items: center !important;
	justify-content: center !important;
	gap: 6px !important;
	white-space: nowrap !important;
	flex-shrink: 0 !important;
}

.color-scheme-generator-button:hover:not(:disabled) {
	transform: translateY(-1px) !important;
	box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3) !important;
	background: linear-gradient(135deg, #20c997 0%, #17a2b8 100%) !important;
}

.color-scheme-generator-button:disabled,
.color-scheme-generator-button.disabled {
	background: linear-gradient(135deg, #6c757d 0%, #5a6268 100%) !important;
	cursor: not-allowed !important;
	opacity: 0.6 !important;
	box-shadow: none !important;
}

.color-scheme-generator-button.disabled:hover {
	transform: none !important;
	background: linear-gradient(135deg, #6c757d 0%, #5a6268 100%) !important;
	box-shadow: none !important;
}

.color-scheme-generator-button i {
	font-size: 14px !important;
}

/* Color Scheme Generator Dialog Styles */
.color-scheme-generator-dialog {
	z-index: 10000 !important;
}

.color-scheme-generator-content {
	padding: 1rem 0;
}

.generator-color-display {
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: #f8f9fa;
	border-radius: 8px;
	border: 1px solid #e9ecef;
}

.generator-color-controls {
	display: flex;
	align-items: center;
	gap: 12px;
	margin-top: 0.5rem;
}

.generator-color-swatch {
	width: 50px;
	height: 50px;
	border-radius: 8px;
	border: 2px solid #ffffff;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
	cursor: pointer;
	transition: all 0.3s ease;
	position: relative;
}

.generator-color-swatch:hover {
	transform: scale(1.05);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.generator-color-hex {
	font-family: "SF Mono", "Monaco", "Inconsolata", "Roboto Mono", monospace;
	font-weight: 600;
	font-size: 14px;
	color: #495057;
	background: #ffffff;
	padding: 8px 12px;
	border-radius: 4px;
	border: 1px solid #e9ecef;
}

.generator-color-picker-panel {
	margin-top: 1rem;
	padding: 1rem;
	background: #ffffff;
	border: 2px solid #28a745;
	border-radius: 8px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.generator-color-picker {
	border: none !important;
	box-shadow: none !important;
}

.generator-color-picker ::v-deep(.p-colorpicker-panel) {
	border: none !important;
	border-radius: 6px !important;
	box-shadow: none !important;
}

.color-scheme-generator-dialog .field-label {
	font-weight: 600;
	color: #374151;
	margin-bottom: 0.5rem;
	display: block;
}

.color-scheme-generator-dialog .p-help {
	color: #6c757d;
	font-style: italic;
	margin-top: 0.5rem;
	line-height: 1.4;
}

/* Color Scheme Explanation Card */
.color-scheme-explanation {
	margin-top: 1.5rem;
	animation: slideDown 0.3s ease-out;
}

.explanation-card {
	background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
	border: 1px solid #d1d5db;
	border-radius: 8px;
	padding: 1rem;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.explanation-card h4 {
	margin: 0 0 0.75rem 0;
	color: #1f2937;
	font-size: 1rem;
	font-weight: 600;
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.explanation-card h4::before {
	content: "ℹ️";
	font-size: 1.1rem;
}

.explanation-card p {
	margin: 0;
	color: #4b5563;
	line-height: 1.6;
	font-size: 0.9rem;
}

@keyframes slideDown {
	from {
		opacity: 0;
		transform: translateY(-10px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

/* Template Selector with Inline Edit Styles */
.template-selector-container {
	display: flex;
	flex-direction: column;
	gap: 0;
}

.selector-with-edit {
	display: flex;
	align-items: center;
	gap: 8px;
}

.template-select {
	flex: 1;
	max-width: 320px;
}

.edit-name-icon-button {
	width: 40px !important;
	height: 40px !important;
	padding: 0 !important;
	display: flex !important;
	align-items: center !important;
	justify-content: center !important;
	border-radius: 8px !important;
	flex-shrink: 0 !important;
	border: 2px solid #6c757d !important;
	background: #ffffff !important;
	color: #495057 !important;
	transition: all 0.3s ease !important;
}

.edit-name-icon-button:hover:not(:disabled) {
	background: #f8f9fa !important;
	border-color: #495057 !important;
	color: #212529 !important;
	transform: scale(1.05) !important;
}

.edit-name-icon-button:disabled {
	opacity: 0.4 !important;
	cursor: not-allowed !important;
}

.edit-name-icon-button i {
	font-size: 16px !important;
}

.edit-name-container {
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
}

.theme-name-input {
	flex: 1;
	min-width: 200px;
	max-width: 320px;
	border-radius: 6px !important;
	border: 1px solid #d1d5db !important;
	padding: 0.5rem 0.75rem !important;
	font-size: 14px !important;
	transition: all 0.3s ease !important;
}

.theme-name-input:focus {
	border-color: #17a2b8 !important;
	box-shadow: 0 0 0 3px rgba(23, 162, 184, 0.1) !important;
}

.save-name-button {
	width: 40px !important;
	height: 40px !important;
	padding: 0 !important;
	display: flex !important;
	align-items: center !important;
	justify-content: center !important;
	border-radius: 8px !important;
	flex-shrink: 0 !important;
	background: linear-gradient(135deg, #28a745 0%, #20c997 100%) !important;
	border: none !important;
	color: white !important;
	box-shadow: 0 2px 8px rgba(40, 167, 69, 0.3) !important;
	transition: all 0.3s ease !important;
}

.save-name-button:hover:not(:disabled) {
	background: linear-gradient(135deg, #20c997 0%, #17a2b8 100%) !important;
	transform: translateY(-1px) scale(1.05) !important;
	box-shadow: 0 4px 12px rgba(40, 167, 69, 0.4) !important;
}

.save-name-button:disabled {
	background: #6c757d !important;
	opacity: 0.5 !important;
	box-shadow: none !important;
}

.save-name-button i {
	font-size: 16px !important;
}

.cancel-name-button {
	padding: 8px 16px !important;
	font-size: 14px !important;
	font-weight: 500 !important;
	border-radius: 8px !important;
	flex-shrink: 0 !important;
	white-space: nowrap !important;
	height: 40px !important;
	border: 2px solid #dc3545 !important;
	background: #ffffff !important;
	color: #dc3545 !important;
	transition: all 0.3s ease !important;
}

.cancel-name-button:hover {
	background: #dc3545 !important;
	color: white !important;
	transform: translateY(-1px) !important;
	box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3) !important;
}

/* Delete Theme Dialog Styles */

.delete-confirm-content {
	padding: 1rem 0;
}

.confirmation-message {
	display: flex;
	gap: 1rem;
	align-items: flex-start;
}

.confirmation-message .warning-icon {
	color: #f59e0b;
	font-size: 2rem;
	margin-top: 0.25rem;
	flex-shrink: 0;
}

.message-text p {
	margin: 0 0 0.75rem 0;
	line-height: 1.5;
}

.message-text p:last-child {
	margin-bottom: 0;
}

.theme-name-display {
	font-style: italic;
	color: #6366f1;
	font-weight: 600;
}

.warning-text {
	color: #6b7280;
	font-size: 0.9rem;
}

/* Delete Theme Button */
.delete-theme-button {
	background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%) !important;
	border: none !important;
	color: white !important;
	padding: 0.5rem 0.75rem !important;
	border-radius: 8px !important;
	font-weight: 600 !important;
	transition: all 0.3s ease !important;
	box-shadow: 0 2px 8px rgba(220, 38, 38, 0.2) !important;
}

.delete-theme-button:hover:not(:disabled) {
	background: linear-gradient(135deg, #b91c1c 0%, #991b1b 100%) !important;
	transform: translateY(-2px) !important;
	box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3) !important;
}

.delete-theme-button:disabled,
.delete-theme-button.disabled {
	background: #9ca3af !important;
	color: #6b7280 !important;
	cursor: not-allowed !important;
	transform: none !important;
	box-shadow: none !important;
}

/* Import/Export Theme Buttons */
.export-theme-button {
	background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
	border: none !important;
	color: white !important;
	padding: 0.5rem 0.75rem !important;
	border-radius: 8px !important;
	font-weight: 600 !important;
	transition: all 0.3s ease !important;
	box-shadow: 0 2px 8px rgba(16, 185, 129, 0.2) !important;
}

.export-theme-button:hover:not(:disabled) {
	background: linear-gradient(135deg, #059669 0%, #047857 100%) !important;
	transform: translateY(-2px) !important;
	box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3) !important;
}

.export-theme-button:disabled {
	background: #9ca3af !important;
	color: #6b7280 !important;
	cursor: not-allowed !important;
	transform: none !important;
	box-shadow: none !important;
}

.import-theme-button {
	background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important;
	border: none !important;
	color: white !important;
	padding: 0.5rem 0.75rem !important;
	border-radius: 8px !important;
	font-weight: 600 !important;
	transition: all 0.3s ease !important;
	box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2) !important;
}

.import-theme-button:hover:not(:disabled) {
	background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%) !important;
	transform: translateY(-2px) !important;
	box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3) !important;
}

.import-theme-button:disabled {
	background: #9ca3af !important;
	color: #6b7280 !important;
	cursor: not-allowed !important;
	transform: none !important;
	box-shadow: none !important;
}

/* Import Confirmation Dialog Styles */
.import-confirm-dialog {
	border-radius: 12px !important;
}

.import-confirm-dialog .p-dialog-content {
	padding: 1.5rem !important;
}

.import-confirm-content {
	display: flex;
	flex-direction: column;
	gap: 1rem;
}

.confirmation-message {
	display: flex;
	align-items: flex-start;
	gap: 1rem;
}

.message-text {
	flex: 1;
}

.warning-icon {
	flex-shrink: 0;
	margin-top: 0.25rem;
}
</style>
